import type { Asset, Transaction, KPI, AuditEntry, Attachment, AssetYearValue, AssetPhoto } from '@/types/asset';
import type { Project, DepreciationBatch } from '@/types/depreciation';
import type { Location, Category, Equipment, DisposalReason, SubCategory } from '@/types/settings';
import type { DisposalPreviewRequest, DisposalPreviewResponse, DisposalSubmitRequest, DisposalSubmitResponse } from '@/types/disposal';
import { mockAssets, mockTransactions, mockKPIs, mockAuditLog } from '@/lib/mock-data';
import { mockProjects, mockBatch } from '@/lib/mock-depreciation-data';
import { mockLocations, mockCategories, mockEquipment, mockDisposalReasons, mockSubCategories, mockSubLocations } from '@/lib/mock-settings-data';
import { getBackendUrl } from '@/hooks/use-get-backend-url';
import { getAuthConfig } from '@/lib/auth-fetch';
import { getApiUrl } from '@/lib/api-config';
import { generateAlertsForAsset, calculateAlertSummary } from './alerts-service';
import { getIntegrationService } from './integration-service-registry';
import { getModuleMode } from '@/lib/feature-mode';

// Helper function to calculate year values for a single year - extracted to reduce cognitive complexity (S3776)
function calculateYearValues(
  year: number,
  yearIndex: number,
  previousYearData: AssetYearValue | null
): AssetYearValue {
  const isFirstYear = yearIndex === 4;
  
  // Calculate additions based on year index - extracted to avoid nested ternary (S3358)
  const getAdditions = (idx: number, isFirst: boolean): number => {
    if (isFirst) return 50000;
    if (idx === 3) return 5000;
    if (idx === 2) return 2500;
    return 0;
  };
  const additions = getAdditions(yearIndex, isFirstYear);
  const disposals = yearIndex === 1 ? 3000 : 0;
  const revaluation = yearIndex === 2 ? 2000 : 0;

  // Calculate cost values
  const costSOY: number = previousYearData ? previousYearData.cost.endOfYear : 0;
  const costEOY = costSOY + additions - disposals + revaluation;

  // Calculate depreciation values
  const depreciationSOY: number = previousYearData ? previousYearData.depreciation.endOfYear : 0;
  const depreciationCharge = costEOY > 0 ? costEOY * 0.20 : 0;
  const depreciationDisposals = yearIndex === 1 ? 600 : 0;
  const depreciationRevaluation = yearIndex === 2 ? 400 : 0;
  const depreciationEOY = depreciationSOY + depreciationCharge - depreciationDisposals + depreciationRevaluation;

  // Calculate net book values
  const nbvSOY = costSOY - depreciationSOY;
  const nbvEOY = costEOY - depreciationEOY;

  return {
    year: year.toString(),
    cost: {
      startOfYear: Math.round(costSOY * 100) / 100,
      additions: Math.round(additions * 100) / 100,
      disposals: Math.round(disposals * 100) / 100,
      revaluation: Math.round(revaluation * 100) / 100,
      endOfYear: Math.round(costEOY * 100) / 100,
    },
    depreciation: {
      startOfYear: Math.round(depreciationSOY * 100) / 100,
      charge: Math.round(depreciationCharge * 100) / 100,
      disposals: Math.round(depreciationDisposals * 100) / 100,
      revaluation: Math.round(depreciationRevaluation * 100) / 100,
      endOfYear: Math.round(depreciationEOY * 100) / 100,
    },
    netBookValue: {
      startOfYear: Math.round(nbvSOY * 100) / 100,
      endOfYear: Math.round(nbvEOY * 100) / 100,
    },
  };
}

// Helper function to generate mock year values for demo mode - extracted to reduce cognitive complexity (S3776)
function generateMockYearValues(): AssetYearValue[] {
  const currentYear = new Date().getFullYear();
  const yearValues: AssetYearValue[] = [];

  for (let i = 4; i >= 0; i--) {
    const year = currentYear - i;
    const previousYearData = i < 4 ? yearValues[yearValues.length - 1] : null;
    yearValues.push(calculateYearValues(year, i, previousYearData));
  }

  return yearValues;
}

// Helper function to get current data source mode from feature settings
// Uses getModuleMode('assets') to be consistent with FinancialWidgetsService
// which uses getModuleMode('dashboard'). This ensures all dashboard widgets
// use the same mode determination logic from feature settings.
const getDataSource = (): 'demo' | 'api' => {
  const mode = getModuleMode('assets');
  if (mode === 'demo') return 'demo';
  return 'api';
};

class DataService {
  private authHeader?: string;

  constructor() {
  }

  /**
   * Transform frontend AssetPhoto to backend CreateAssetImageDto format.
   * Extracts base64 data from data URL and maps fields appropriately.
   * 
   * Only returns photos that have data URLs (new uploads). Photos with regular
   * URLs (existing photos from the API) are filtered out since the backend
   * expects base64 data for new uploads.
   * 
   * Returns undefined if there are no new photos to upload, which tells the
   * backend to preserve existing photos.
   */
  private transformPhotosToImages(photos: Array<{
    id: string;
    url: string;
    isPrimary: boolean;
    uploadedDate: string;
    fileName?: string;
    fileSize?: number;
  }> | undefined): Array<{
    fileName: string;
    contentType: string;
    base64Data: string;
    isPrimary: boolean;
  }> | undefined {
    if (!photos || photos.length === 0) {
      return undefined;
    }

    // Only process photos with data URLs (new uploads)
    const newPhotos = photos.filter(photo => photo.url.startsWith('data:'));
    
    // If there are no new photos to upload, return undefined to preserve existing photos
    if (newPhotos.length === 0) {
      return undefined;
    }

    return newPhotos.map(photo => {
      // Extract base64 data and content type from data URL
      // Format: data:image/jpeg;base64,/9j/4AAQSkZJRg...
      let base64Data = '';
      let contentType = 'image/jpeg';
      
      // Use string operations instead of regex to avoid stack overflow on large base64 strings
      // The regex .+ pattern can cause "Maximum call stack size exceeded" on large images
      const commaIndex = photo.url.indexOf(',');
      if (commaIndex !== -1) {
        const prefix = photo.url.substring(0, commaIndex);
        const prefixMatch = /^data:([^;]+);base64$/.exec(prefix);
        if (prefixMatch) {
          contentType = prefixMatch[1];
          base64Data = photo.url.substring(commaIndex + 1);
        }
      }

      return {
        fileName: photo.fileName ?? `image-${photo.id}.jpg`,
        contentType,
        base64Data,
        isPrimary: photo.isPrimary,
      };
    }).filter(img => img.base64Data !== ''); // Filter out any photos that failed to parse
  }

  /**
   * Calculate incremental image changes for update operations.
   * Compares current photos with original photos to determine:
   * - imagesToAdd: New photos (not in original photos by ID)
   * - imageIdsToDelete: IDs of photos that were removed
   * - primaryImageId: ID of the image to set as primary (if changed on an existing image)
   * 
   * This enables incremental updates where users can add/remove individual
   * images without affecting other images.
   * 
   * Note: The backend returns all images with data URLs (base64 encoded), so we cannot
   * use URL format to distinguish new vs existing photos. Instead, we compare photo IDs
   * between current and original to determine what changed.
   */
  private calculateImageChanges(
    currentPhotos: Array<{
      id: string;
      url: string;
      isPrimary: boolean;
      uploadedDate: string;
      fileName?: string;
      fileSize?: number;
    }> | undefined,
    originalPhotos: Array<{
      id: string;
      url: string;
      isPrimary: boolean;
      uploadedDate: string;
      fileName?: string;
      fileSize?: number;
    }> | undefined
  ): {
    imagesToAdd: Array<{
      fileName: string;
      contentType: string;
      base64Data: string;
      isPrimary: boolean;
    }> | undefined;
    imageIdsToDelete: string[] | undefined;
    primaryImageId: string | undefined;
  } {
    const current = currentPhotos ?? [];
    const original = originalPhotos ?? [];
    
    // Build sets of IDs for efficient lookup
    const originalIds = new Set(original.map(p => p.id));
    const currentIds = new Set(current.map(p => p.id));
    
    // Find photos to add (photos in current that are NOT in original by ID)
    // These are newly uploaded photos that need to be sent to the backend
    const photosToAdd = current.filter(photo => !originalIds.has(photo.id));
    const imagesToAdd = photosToAdd.length > 0 
      ? photosToAdd.map(photo => {
          let base64Data = '';
          let contentType = 'image/jpeg';
          
          // Extract base64 data from data URL using string operations instead of regex
          // to avoid stack overflow on large base64 strings (the regex .+ pattern can
          // cause "Maximum call stack size exceeded" on large images)
          const commaIndex = photo.url.indexOf(',');
          if (commaIndex !== -1) {
            const prefix = photo.url.substring(0, commaIndex);
            const prefixMatch = /^data:([^;]+);base64$/.exec(prefix);
            if (prefixMatch) {
              contentType = prefixMatch[1];
              base64Data = photo.url.substring(commaIndex + 1);
            }
          }

          return {
            fileName: photo.fileName ?? `image-${photo.id}.jpg`,
            contentType,
            base64Data,
            isPrimary: photo.isPrimary,
          };
        }).filter(img => img.base64Data !== '')
      : undefined;
    
    // Find photos to delete (photos in original that are NOT in current by ID)
    const deletedIds = original
      .filter(photo => !currentIds.has(photo.id))
      .map(photo => photo.id);
    
    const imageIdsToDelete = deletedIds.length > 0 ? deletedIds : undefined;
    
    // Find if the primary image has changed on an existing image (not a new upload)
    // This handles the case where user marks an existing image as primary
    const currentPrimaryPhoto = current.find(p => p.isPrimary && originalIds.has(p.id));
    const originalPrimaryPhoto = original.find(p => p.isPrimary);
    
    // Only set primaryImageId if:
    // 1. There's a current primary photo that's an existing image (not a new upload)
    // 2. AND either there was no original primary, or the primary has changed
    let primaryImageId: string | undefined = undefined;
    if (currentPrimaryPhoto && (!originalPrimaryPhoto || currentPrimaryPhoto.id !== originalPrimaryPhoto.id)) {
      primaryImageId = currentPrimaryPhoto.id;
    }
    
    return {
      imagesToAdd: (imagesToAdd?.length ?? 0) > 0 ? imagesToAdd : undefined,
      imageIdsToDelete,
      primaryImageId,
    };
  }

  /**
   * Transform backend AssetDto to frontend Asset type
   * Maps AssetId (backend) to code (frontend)
   * Maps images (backend) to photos (frontend)
   */
  private transformAssetFromDto(dto: any): Asset {
    // Map backend images array to frontend photos array
    const photos = dto.images?.map((img: any) => ({
      id: img.id,
      url: img.url,
      isPrimary: img.isPrimary,
      uploadedDate: img.uploadedDate,
      fileName: img.fileName,
      fileSize: img.fileSize,
    })) ?? undefined;

    return {
      ...dto,
      code: dto.assetId || dto.code, // Map AssetId from backend to code for frontend
      photos, // Map backend images to frontend photos
    };
  }

  /**
   * Transform array of backend AssetDto to frontend Asset types
   */
  private transformAssetsFromDto(dtos: any[]): Asset[] {
    return dtos.map(dto => this.transformAssetFromDto(dto));
  }

  private getApiBaseUrl(): string {
    const apiUrlFromConfig = getApiUrl();
    const envUrl = import.meta.env.VITE_API_BASE_URL;
    const backendUrl = getBackendUrl();
    const isE2B = typeof window !== 'undefined' && window.location.hostname.includes('.e2b.app');

    console.log('[DataService] API URL sources:', {
      apiUrlFromConfig,
      envUrl,
      backendUrl,
      hostname: typeof window !== 'undefined' ? window.location.hostname : 'N/A',
      isE2B
    });

    // In E2B environment, always use the detected backend URL
    // Otherwise, use the configured URL or env URL as fallback
    const baseUrl = isE2B ? backendUrl : (apiUrlFromConfig ?? envUrl ?? backendUrl);
    console.log('[DataService] ✅ Using base URL:', baseUrl);

    try {
      const url = new URL(baseUrl);
      if (url.username || url.password) {
        const credentials = btoa(`${url.username}:${url.password}`);
        this.authHeader = `Basic ${credentials}`;
        url.username = '';
        url.password = '';
        return url.toString().replace(/\/$/, '');
      }
      return baseUrl.replace(/\/$/, '');
    } catch (e) {
      return baseUrl.replace(/\/$/, '');
    }
  }

  private async buildHeaders(options?: RequestInit): Promise<Record<string, string>> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options?.headers as Record<string, string>),
    };

    if (this.authHeader) {
      headers['Authorization'] = this.authHeader;
    }

    const authConfig = getAuthConfig();
    if (authConfig.getAccessToken) {
      try {
        const token = await authConfig.getAccessToken();
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      } catch (error) {
        console.error('Error getting access token:', error);
      }
    }

    headers['X-Instance-Id'] = authConfig.instanceId ?? 'default-instance';
    return headers;
  }

  private async parseErrorResponse(response: Response): Promise<string> {
    let errorMessage = `API error: ${response.status} ${response.statusText}`;
    try {
      const errorData = await response.json();
      if (errorData.message) {
        errorMessage = errorData.message;
      }
    } catch {
    }
    return errorMessage;
  }

  private async fetchAPI(endpoint: string, options?: RequestInit) {
    const apiBaseUrl = this.getApiBaseUrl();
    const headers = await this.buildHeaders(options);

    try {
      const response = await fetch(`${apiBaseUrl}${endpoint}`, { ...options, headers });

      if (!response.ok) {
        throw new Error(await this.parseErrorResponse(response));
      }

      return response.status === 204 ? null : response.json();
    } catch (error) {
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error(`Unable to connect to API at ${apiBaseUrl}. Please ensure the backend server is running or switch to Demo mode.`);
      }
      throw error;
    }
  }

  /**
   * Helper method to wrap API calls with error handling.
   * In live mode, if the API call fails, throws a user-friendly error.
   * @param apiCall - The API call to execute
   * @param demoFallback - Function that returns demo data as fallback (kept for backwards compatibility but not used)
   * @param methodName - Name of the method for logging purposes
   */
  private async withDemoFallback<T>(
    apiCall: () => Promise<T>,
    demoFallback: () => T | Promise<T>,
    methodName: string
  ): Promise<T> {
    try {
      return await apiCall();
    } catch (error) {
      console.error(`[DataService] ${methodName} API call failed:`, error);
      // Throw a user-friendly error instead of silently falling back to demo data
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
      throw new Error(`Failed to ${methodName.replace(/([A-Z])/g, ' $1').toLowerCase().trim()}: ${errorMessage}`);
    }
  }

  async getAssets(filters?: {
    page?: number;
    pageSize?: number;
    status?: string;
    category?: string;
    location?: string;
    search?: string;
    sortBy?: string;
    order?: string;
  }): Promise<{ data: Asset[]; total: number; page: number; pageSize: number; totalPages: number }> {
    if (getDataSource() === 'demo') {
      let filteredAssets = [...mockAssets];

      if (filters?.status) {
        filteredAssets = filteredAssets.filter(a => a.status === filters.status);
      }
      if (filters?.category) {
        filteredAssets = filteredAssets.filter(a => a.category === filters.category);
      }
      if (filters?.location) {
        filteredAssets = filteredAssets.filter(a => a.location === filters.location);
      }
      if (filters?.search) {
        const search = filters.search.toLowerCase();
        filteredAssets = filteredAssets.filter(a =>
          a.name.toLowerCase().includes(search) ||
          (a.description && a.description.toLowerCase().includes(search)) ||
          (a.code && a.code.toLowerCase().includes(search))
        );
      }

      if (filters?.sortBy) {
        const sortBy = filters.sortBy as keyof Asset;
        const order = filters.order ?? 'asc';
        filteredAssets.sort((a, b) => {
          const aVal = a[sortBy];
          const bVal = b[sortBy];
          if (aVal === undefined || bVal === undefined) return 0;
          if (aVal < bVal) return order === 'asc' ? -1 : 1;
          if (aVal > bVal) return order === 'asc' ? 1 : -1;
          return 0;
        });
      }

      // Enrich assets with alert summaries
      const enrichedAssets = filteredAssets.map(asset => {
        const alerts = generateAlertsForAsset(asset);
        const alertSummary = calculateAlertSummary(alerts);
        return {
          ...asset,
          alertSummary: alertSummary.totalCount > 0 ? alertSummary : undefined,
        };
      });

      const page = filters?.page ?? 1;
      const pageSize = filters?.pageSize ?? 50;
      const start = (page - 1) * pageSize;
      const end = start + pageSize;

      return {
        data: enrichedAssets.slice(start, end),
        total: enrichedAssets.length,
        page,
        pageSize,
        totalPages: Math.ceil(enrichedAssets.length / pageSize),
      };
    }

    const queryString = new URLSearchParams(
      Object.entries(filters ?? {}).reduce((acc, [key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          acc[key] = String(value);
        }
        return acc;
      }, {} as Record<string, string>)
    ).toString();

    // In live mode, call API with demo fallback on error
    // Use the lightweight /api/Assets/list endpoint for better performance
    return this.withDemoFallback(
      async () => {
        const response = await this.fetchAPI(`/api/Assets/list?${queryString}`);
        return {
          ...response,
          data: this.transformAssetsFromDto(response.data ?? []),
        };
      },
      () => this.getDemoAssets(filters),
      'getAssets'
    );
  }

  /**
   * Get assets from API only (no demo fallback).
   * Use this when you want to control the fallback behavior at the page level.
   * Throws on error instead of falling back to demo data.
   * Uses the lightweight /api/Assets/list endpoint for better performance.
   */
  async getAssetsApiOnly(filters?: {
    page?: number;
    pageSize?: number;
    status?: string;
    category?: string;
    location?: string;
    search?: string;
    sortBy?: string;
    order?: string;
  }): Promise<{ data: Asset[]; total: number; page: number; pageSize: number; totalPages: number }> {
    const queryString = new URLSearchParams(
      Object.entries(filters ?? {}).reduce((acc, [key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          acc[key] = String(value);
        }
        return acc;
      }, {} as Record<string, string>)
    ).toString();

    // Use the lightweight /api/Assets/list endpoint for better performance
    const response = await this.fetchAPI(`/api/Assets/list?${queryString}`);
    return {
      ...response,
      data: this.transformAssetsFromDto(response.data ?? []),
    };
  }

  /**
   * Get demo assets data.
   * Use this for explicit demo mode or as a fallback when API fails.
   */
  getDemoAssets(filters?: {
    page?: number;
    pageSize?: number;
    status?: string;
    category?: string;
    location?: string;
    search?: string;
    sortBy?: string;
    order?: string;
  }): { data: Asset[]; total: number; page: number; pageSize: number; totalPages: number } {
    let filteredAssets = [...mockAssets];

    if (filters?.status) {
      filteredAssets = filteredAssets.filter(a => a.status === filters.status);
    }
    if (filters?.category) {
      filteredAssets = filteredAssets.filter(a => a.category === filters.category);
    }
    if (filters?.location) {
      filteredAssets = filteredAssets.filter(a => a.location === filters.location);
    }
    if (filters?.search) {
      const search = filters.search.toLowerCase();
      filteredAssets = filteredAssets.filter(a =>
        a.name.toLowerCase().includes(search) ||
        a.description?.toLowerCase().includes(search)
      );
    }

    if (filters?.sortBy) {
      const sortBy = filters.sortBy as keyof Asset;
      const order = filters.order ?? 'asc';
      filteredAssets.sort((a, b) => {
        const aVal = a[sortBy];
        const bVal = b[sortBy];
        if (aVal === undefined || bVal === undefined) return 0;
        if (aVal < bVal) return order === 'asc' ? -1 : 1;
        if (aVal > bVal) return order === 'asc' ? 1 : -1;
        return 0;
      });
    }

    // Enrich assets with alert summaries
    const enrichedAssets = filteredAssets.map(asset => {
      const alerts = generateAlertsForAsset(asset);
      const alertSummary = calculateAlertSummary(alerts);
      return {
        ...asset,
        alertSummary: alertSummary.totalCount > 0 ? alertSummary : undefined,
      };
    });

    const page = filters?.page ?? 1;
    const pageSize = filters?.pageSize ?? 50;
    const start = (page - 1) * pageSize;
    const end = start + pageSize;

    return {
      data: enrichedAssets.slice(start, end),
      total: enrichedAssets.length,
      page,
      pageSize,
      totalPages: Math.ceil(enrichedAssets.length / pageSize),
    };
  }

  async getAsset(id: string): Promise<Asset> {
    if (getDataSource() === 'demo') {
      const asset = mockAssets.find(a => a.id === id);
      if (!asset) throw new Error('Asset not found');
      return asset;
    }
    // In live mode, call API with demo fallback on error
    return this.withDemoFallback(
      async () => {
        const dto = await this.fetchAPI(`/api/Assets/${id}`);
        return this.transformAssetFromDto(dto);
      },
      () => {
        const asset = mockAssets.find(a => a.id === id);
        if (!asset) throw new Error('Asset not found');
        return asset;
      },
      'getAsset'
    );
  }

  async createAsset(asset: Partial<Asset>): Promise<Asset> {
    if (getDataSource() === 'demo') {
      throw new Error('Cannot create assets in demo mode. Please switch to API mode.');
    }

    // Transform Asset to CreateAssetDto
    const createDto = {
      code: asset.code ?? '',
      name: asset.name ?? '',
      description: asset.description,
      category: asset.category ?? '',
      subCategory: asset.subCategory,
      location: asset.location ?? '',
      subLocation: asset.subLocation,
      status: asset.status ?? 'active',
      acquisitionCost: asset.acquisitionCost ?? 0,
      currency: asset.currency ?? 'GBP',
      acquisitionDate: asset.acquisitionDate ?? new Date().toISOString(),
      depreciationMethod: asset.depreciationMethod,
      usefulLife: asset.usefulLife,
      residualValue: asset.residualValue,
      serialNumber: asset.serialNumber,
      manufacturer: asset.manufacturer,
      model: asset.model,
      barcode: asset.barcode,
      barcodeType: asset.barcodeType,
      barcodeValue: asset.barcodeValue,
      assignedTo: asset.assignedTo,
      assignedDate: asset.assignedDate,
      insurancePolicyNumber: asset.insurancePolicyNumber,
      insuranceProvider: asset.insuranceProvider,
      insuranceExpiryDate: asset.insuranceExpiryDate,
      insuranceValue: asset.insuranceValue,
      warrantyExpiryDate: asset.warrantyExpiryDate,
      warrantyProvider: asset.warrantyProvider,
      supplierId: asset.supplierId,
      supplierName: asset.supplierName,
      purchaseOrderNumber: asset.purchaseOrderNumber,
      parentAssetId: asset.parentAssetId,
      notes: asset.notes,
      createdBy: asset.createdBy ?? 'system',
      importJobId: asset.importJobId,
      // Depreciation tracking fields (CHG-003 & CHG-004)
      depreciationPercentage: asset.depreciationPercentage,
      depreciationYears: asset.depreciationYears,
      postingFrequency: asset.postingFrequency,
      inServicePeriod: asset.inServicePeriod,
      inServiceYear: asset.inServiceYear,
      nextPostingPeriod: asset.nextPostingPeriod,
      nextPostingYear: asset.nextPostingYear,
      postingsMade: asset.postingsMade,
      totalPostings: asset.totalPostings,
      doNotDepreciate: asset.doNotDepreciate,
      baseValueOn: asset.baseValueOn,
      currentBaseValue: asset.currentBaseValue,
      // Custom user fields
      userKey1: asset.userKey1,
      userKey2: asset.userKey2,
      userKey3: asset.userKey3,
      userKey4: asset.userKey4,
      userKey5: asset.userKey5,
      userValue1: asset.userValue1,
      userValue2: asset.userValue2,
      userValue3: asset.userValue3,
      userValue4: asset.userValue4,
      userValue5: asset.userValue5,
      userDate1: asset.userDate1,
      userDate2: asset.userDate2,
      userDate3: asset.userDate3,
      // Additional compliance fields
      insuranceStartDate: asset.insuranceStartDate,
      insuranceNotes: asset.insuranceNotes,
      warrantyStartDate: asset.warrantyStartDate,
      warrantyTerms: asset.warrantyTerms,
      warrantyNotes: asset.warrantyNotes,
      warrantyCertificationUrl: asset.warrantyCertificationUrl,
      // Additional overview fields
      quantity: asset.quantity,
      resourceCode: asset.resourceCode,
      subLedger: asset.subLedger,
      registrationNumber: asset.registrationNumber,
      invoiceReference: asset.invoiceReference,
      hirePurchase: asset.hirePurchase,
      // Nominal account fields
      assetCostNominal: asset.assetCostNominal,
      depreciationBSNominal: asset.depreciationBSNominal,
      depreciationPLNominal: asset.depreciationPLNominal,
      disposalsNominal: asset.disposalsNominal,
      revaluationBSNominal: asset.revaluationBSNominal,
      revaluationPLNominal: asset.revaluationPLNominal,
      disposalsBankAccountNominal: asset.disposalsBankAccountNominal,
      additionsWIPBankAccountNominal: asset.additionsWIPBankAccountNominal,
      // Costing fields
      project: asset.project,
      costCentre: asset.costCentre,
      tags: asset.tags ?? [],
      // Images - transform frontend photos to backend format
      images: this.transformPhotosToImages(asset.photos),
    };

    const dto = await this.fetchAPI('/api/Assets', {
      method: 'POST',
      body: JSON.stringify(createDto),
    });
    return this.transformAssetFromDto(dto);
  }

  /**
   * Update an existing asset.
   * @param id - The asset ID
   * @param asset - The asset data to update
   * @param originalPhotos - Optional original photos for incremental image updates.
   *                         When provided, only image changes (additions/deletions) are sent.
   *                         When not provided, falls back to legacy behavior.
   */
  async updateAsset(id: string, asset: Partial<Asset>, originalPhotos?: AssetPhoto[]): Promise<Asset> {
    if (getDataSource() === 'demo') {
      throw new Error('Cannot update assets in demo mode. Please switch to API mode.');
    }

    // Calculate image changes for incremental updates
    const imageChanges = originalPhotos !== undefined
      ? this.calculateImageChanges(asset.photos, originalPhotos)
      : { imagesToAdd: undefined, imageIdsToDelete: undefined, primaryImageId: undefined };

    // Transform Asset to UpdateAssetDto (all fields are optional)
    const updateDto = {
      name: asset.name,
      description: asset.description,
      category: asset.category,
      subCategory: asset.subCategory,
      location: asset.location,
      subLocation: asset.subLocation,
      status: asset.status,
      acquisitionCost: asset.acquisitionCost,
      currency: asset.currency,
      acquisitionDate: asset.acquisitionDate,
      depreciationMethod: asset.depreciationMethod,
      usefulLife: asset.usefulLife,
      residualValue: asset.residualValue,
      serialNumber: asset.serialNumber,
      manufacturer: asset.manufacturer,
      model: asset.model,
      barcode: asset.barcode,
      barcodeType: asset.barcodeType,
      barcodeValue: asset.barcodeValue,
      assignedTo: asset.assignedTo,
      assignedDate: asset.assignedDate,
      insurancePolicyNumber: asset.insurancePolicyNumber,
      insuranceProvider: asset.insuranceProvider,
      insuranceExpiryDate: asset.insuranceExpiryDate,
      insuranceValue: asset.insuranceValue,
      warrantyExpiryDate: asset.warrantyExpiryDate,
      warrantyProvider: asset.warrantyProvider,
      supplierId: asset.supplierId,
      supplierName: asset.supplierName,
      purchaseOrderNumber: asset.purchaseOrderNumber,
      parentAssetId: asset.parentAssetId,
      notes: asset.notes,
      modifiedBy: asset.modifiedBy ?? 'system',
      // Depreciation tracking fields (CHG-003 & CHG-004)
      depreciationPercentage: asset.depreciationPercentage,
      depreciationYears: asset.depreciationYears,
      postingFrequency: asset.postingFrequency,
      inServicePeriod: asset.inServicePeriod,
      inServiceYear: asset.inServiceYear,
      nextPostingPeriod: asset.nextPostingPeriod,
      nextPostingYear: asset.nextPostingYear,
      postingsMade: asset.postingsMade,
      totalPostings: asset.totalPostings,
      doNotDepreciate: asset.doNotDepreciate,
      baseValueOn: asset.baseValueOn,
      currentBaseValue: asset.currentBaseValue,
      // Custom user fields
      userKey1: asset.userKey1,
      userKey2: asset.userKey2,
      userKey3: asset.userKey3,
      userKey4: asset.userKey4,
      userKey5: asset.userKey5,
      userValue1: asset.userValue1,
      userValue2: asset.userValue2,
      userValue3: asset.userValue3,
      userValue4: asset.userValue4,
      userValue5: asset.userValue5,
      userDate1: asset.userDate1,
      userDate2: asset.userDate2,
      userDate3: asset.userDate3,
      // Additional compliance fields
      insuranceStartDate: asset.insuranceStartDate,
      insuranceNotes: asset.insuranceNotes,
      warrantyStartDate: asset.warrantyStartDate,
      warrantyTerms: asset.warrantyTerms,
      warrantyNotes: asset.warrantyNotes,
      warrantyCertificationUrl: asset.warrantyCertificationUrl,
      // Additional overview fields
      quantity: asset.quantity,
      resourceCode: asset.resourceCode,
      subLedger: asset.subLedger,
      registrationNumber: asset.registrationNumber,
      invoiceReference: asset.invoiceReference,
      hirePurchase: asset.hirePurchase,
      // Nominal account fields
      assetCostNominal: asset.assetCostNominal,
      depreciationBSNominal: asset.depreciationBSNominal,
      depreciationPLNominal: asset.depreciationPLNominal,
      disposalsNominal: asset.disposalsNominal,
      revaluationBSNominal: asset.revaluationBSNominal,
      revaluationPLNominal: asset.revaluationPLNominal,
      disposalsBankAccountNominal: asset.disposalsBankAccountNominal,
      additionsWIPBankAccountNominal: asset.additionsWIPBankAccountNominal,
      // Costing fields
      project: asset.project,
      costCentre: asset.costCentre,
      // Images - use incremental updates when originalPhotos is provided, otherwise use legacy behavior
      images: originalPhotos !== undefined ? undefined : this.transformPhotosToImages(asset.photos),
      imagesToAdd: imageChanges.imagesToAdd,
      imageIdsToDelete: imageChanges.imageIdsToDelete,
      // Primary image ID - allows changing the primary image without re-uploading
      primaryImageId: imageChanges.primaryImageId,
    };

    const dto = await this.fetchAPI(`/api/Assets/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updateDto),
    });
    return this.transformAssetFromDto(dto);
  }

  async deleteAsset(id: string): Promise<void> {
    if (getDataSource() === 'demo') {
      throw new Error('Cannot delete assets in demo mode. Please switch to API mode.');
    }
    await this.fetchAPI(`/api/Assets/${id}`, { method: 'DELETE' });
  }

  async getAssetAttachments(id: string): Promise<Attachment[]> {
    if (getDataSource() === 'demo') {
      const asset = mockAssets.find(a => a.id === id);
      return asset?.attachments ?? [];
    }
    return this.fetchAPI(`/api/Assets/${id}/attachments`);
  }

  async getAssetAudit(id: string): Promise<AuditEntry[]> {
    if (getDataSource() === 'demo') {
      return mockAuditLog.filter(entry => entry.assetId === id);
    }
    return this.fetchAPI(`/api/Assets/${id}/audit`);
  }

  async getTransactions(filters?: {
    page?: number;
    pageSize?: number;
    status?: string;
    type?: string;
    assetId?: string;
  }): Promise<{ data: Transaction[]; total: number; page: number; pageSize: number; totalPages: number }> {
    if (getDataSource() === 'demo') {
      let filteredTransactions = [...mockTransactions];

      if (filters?.status) {
        filteredTransactions = filteredTransactions.filter(t => t.status === filters.status);
      }
      if (filters?.type) {
        filteredTransactions = filteredTransactions.filter(t => t.type === filters.type);
      }
      if (filters?.assetId) {
        filteredTransactions = filteredTransactions.filter(t => t.assetId === filters.assetId);
      }

      const page = filters?.page ?? 1;
      const pageSize = filters?.pageSize ?? 50;
      const start = (page - 1) * pageSize;
      const end = start + pageSize;

      return {
        data: filteredTransactions.slice(start, end),
        total: filteredTransactions.length,
        page,
        pageSize,
        totalPages: Math.ceil(filteredTransactions.length / pageSize),
      };
    }

    const queryString = new URLSearchParams(
      Object.entries(filters ?? {}).reduce((acc, [key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          acc[key] = String(value);
        }
        return acc;
      }, {} as Record<string, string>)
    ).toString();

    // In live mode, call API with demo fallback on error
    return this.withDemoFallback(
      () => this.fetchAPI(`/api/Transactions?${queryString}`),
      () => this.getDemoTransactions(filters),
      'getTransactions'
    );
  }

  /**
   * Get transactions from API only (no demo fallback).
   * Use this when you want to control the fallback behavior at the page level.
   * Throws on error instead of falling back to demo data.
   */
  async getTransactionsApiOnly(filters?: {
    page?: number;
    pageSize?: number;
    status?: string;
    type?: string;
    assetId?: string;
  }): Promise<{ data: Transaction[]; total: number; page: number; pageSize: number; totalPages: number }> {
    const queryString = new URLSearchParams(
      Object.entries(filters ?? {}).reduce((acc, [key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          acc[key] = String(value);
        }
        return acc;
      }, {} as Record<string, string>)
    ).toString();

    return this.fetchAPI(`/api/Transactions?${queryString}`);
  }

  /**
   * Get demo transactions data.
   * Use this for explicit demo mode or as a fallback when API fails.
   */
  getDemoTransactions(filters?: {
    page?: number;
    pageSize?: number;
    status?: string;
    type?: string;
    assetId?: string;
  }): { data: Transaction[]; total: number; page: number; pageSize: number; totalPages: number } {
    let filteredTransactions = [...mockTransactions];

    if (filters?.status) {
      filteredTransactions = filteredTransactions.filter(t => t.status === filters.status);
    }
    if (filters?.type) {
      filteredTransactions = filteredTransactions.filter(t => t.type === filters.type);
    }
    if (filters?.assetId) {
      filteredTransactions = filteredTransactions.filter(t => t.assetId === filters.assetId);
    }

    const page = filters?.page ?? 1;
    const pageSize = filters?.pageSize ?? 50;
    const start = (page - 1) * pageSize;
    const end = start + pageSize;

    return {
      data: filteredTransactions.slice(start, end),
      total: filteredTransactions.length,
      page,
      pageSize,
      totalPages: Math.ceil(filteredTransactions.length / pageSize),
    };
  }

  async getTransaction(id: string): Promise<Transaction> {
    if (getDataSource() === 'demo') {
      const transaction = mockTransactions.find(t => t.id === id);
      if (!transaction) throw new Error('Transaction not found');
      return transaction;
    }
    // In live mode, call API with demo fallback on error
    return this.withDemoFallback(
      () => this.fetchAPI(`/api/Transactions/${id}`),
      () => {
        const transaction = mockTransactions.find(t => t.id === id);
        if (!transaction) throw new Error('Transaction not found');
        return transaction;
      },
      'getTransaction'
    );
  }

  async createTransaction(transaction: Partial<Transaction>): Promise<Transaction> {
    if (getDataSource() === 'demo') {
      throw new Error('Cannot create transactions in demo mode. Please switch to API mode.');
    }
    return this.fetchAPI('/api/Transactions', {
      method: 'POST',
      body: JSON.stringify(transaction),
    });
  }

  async getDashboardKPIs(): Promise<KPI[]> {
    if (getDataSource() === 'demo') {
      return mockKPIs;
    }
    // In live mode, call API with demo fallback on error
    return this.withDemoFallback(
      () => this.fetchAPI('/api/Dashboard/kpis'),
      () => mockKPIs,
      'getDashboardKPIs'
    );
  }

  /**
   * Get dashboard KPIs from API only (no demo fallback).
   */
  async getDashboardKPIsApiOnly(): Promise<KPI[]> {
    return this.fetchAPI('/api/Dashboard/kpis');
  }

  /**
   * Get demo dashboard KPIs.
   */
  getDemoDashboardKPIs(): KPI[] {
    return mockKPIs;
  }

  async getAssetsDueForReview(): Promise<{
    totalCount: number;
    depreciationCount: number;
    insuranceCount: number;
    warrantyCount: number;
  }> {
    if (getDataSource() === 'demo') {
      const { assetsDueForReviewBreakdown } = await import('@/lib/mock-data');
      return assetsDueForReviewBreakdown;
    }
    // In live mode, call API with demo fallback on error
    return this.withDemoFallback(
      () => this.fetchAPI('/api/Dashboard/assets-due-for-review'),
      async () => {
        const { assetsDueForReviewBreakdown } = await import('@/lib/mock-data');
        return assetsDueForReviewBreakdown;
      },
      'getAssetsDueForReview'
    );
  }

  async getUpcomingInsuranceRenewals(daysAhead: number = 90): Promise<{
    assetId: string;
    assetCode: string;
    assetName: string;
    renewalDate: string;
    insuranceProvider?: string;
    insurancePolicyNumber?: string;
    insuranceValue?: number;
  }[]> {
    if (getDataSource() === 'demo') {
      const { mockAssets } = await import('@/lib/mock-data');
      const today = new Date();
      const futureDate = new Date(today.getTime() + daysAhead * 24 * 60 * 60 * 1000);

      return mockAssets
        .filter(asset => {
          if (!asset.insuranceExpiryDate) return false;
          const expiryDate = new Date(asset.insuranceExpiryDate);
          return expiryDate >= today && expiryDate <= futureDate;
        })
                .map(asset => ({
                  assetId: asset.id,
                  assetCode: asset.code,
                  assetName: asset.name,
          renewalDate: asset.insuranceExpiryDate!,
          insuranceProvider: asset.insuranceProvider,
          insurancePolicyNumber: asset.insurancePolicyNumber,
          insuranceValue: asset.insuranceValue,
        }))
        .sort((a, b) => new Date(a.renewalDate).getTime() - new Date(b.renewalDate).getTime());
    }
    return this.fetchAPI(`/api/Dashboard/insurance-renewals?daysAhead=${daysAhead}`);
  }

  async getNetBookValueTrend(): Promise<{
    currentValue: number;
    change: number;
    trend: string;
    monthlyData: { month: string; value: number }[];
  }> {
    if (getDataSource() === 'demo') {
      const { netBookValueData } = await import('@/lib/mock-data');
      return {
        currentValue: netBookValueData.currentValue,
        change: 5.2,
        trend: 'up',
        monthlyData: netBookValueData.monthlyData,
      };
    }
    // In live mode, call API with demo fallback on error
    return this.withDemoFallback(
      () => this.fetchAPI('/api/Dashboard/net-book-value-trend'),
      async () => {
        const { netBookValueData } = await import('@/lib/mock-data');
        return {
          currentValue: netBookValueData.currentValue,
          change: 5.2,
          trend: 'up',
          monthlyData: netBookValueData.monthlyData,
        };
      },
      'getNetBookValueTrend'
    );
  }

  async getDepreciationYTDTrend(): Promise<{
    currentValue: number;
    change: number;
    trend: string;
    monthlyData: { month: string; value: number }[];
  }> {
    if (getDataSource() === 'demo') {
      const { depreciationYTDData } = await import('@/lib/mock-data');
      return {
        currentValue: depreciationYTDData.currentValue,
        change: -2.3,
        trend: 'down',
        monthlyData: depreciationYTDData.monthlyData,
      };
    }
    // In live mode, call API with demo fallback on error
    return this.withDemoFallback(
      () => this.fetchAPI('/api/Dashboard/depreciation-ytd-trend'),
      async () => {
        const { depreciationYTDData } = await import('@/lib/mock-data');
        return {
          currentValue: depreciationYTDData.currentValue,
          change: -2.3,
          trend: 'down',
          monthlyData: depreciationYTDData.monthlyData,
        };
      },
      'getDepreciationYTDTrend'
    );
  }

  async getDepreciationProjects(): Promise<Project[]> {
    if (getDataSource() === 'demo') {
      return mockProjects;
    }
    return this.fetchAPI('/api/Depreciation/projects');
  }

  async getDepreciationProject(id: string): Promise<Project> {
    if (getDataSource() === 'demo') {
      const project = mockProjects.find(p => p.id === id);
      if (!project) throw new Error('Project not found');
      return project;
    }
    return this.fetchAPI(`/api/Depreciation/projects/${id}`);
  }

  async getDepreciationBatches(filters?: {
    projectId?: string;
    status?: string;
  }): Promise<DepreciationBatch[]> {
    if (getDataSource() === 'demo') {
      const batches = [mockBatch];
      let filteredBatches = [...batches];

      if (filters?.projectId) {
        filteredBatches = filteredBatches.filter(b => b.projectId === filters.projectId);
      }
      if (filters?.status) {
        filteredBatches = filteredBatches.filter(b => b.status === filters.status);
      }

      return filteredBatches;
    }

    const queryString = new URLSearchParams(
      Object.entries(filters ?? {}).reduce((acc, [key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          acc[key] = String(value);
        }
        return acc;
      }, {} as Record<string, string>)
    ).toString();

    return this.fetchAPI(`/api/Depreciation/batches?${queryString}`);
  }

  async getDepreciationBatch(id: string): Promise<DepreciationBatch> {
    if (getDataSource() === 'demo') {
      if (mockBatch.id === id) {
        return mockBatch;
      }
      throw new Error('Batch not found');
    }
    return this.fetchAPI(`/api/Depreciation/batches/${id}`);
  }

  async createDepreciationBatch(batch: any): Promise<any> {
    if (getDataSource() === 'demo') {
      throw new Error('Cannot create depreciation batch in demo mode. Please switch to API mode.');
    }
    return this.fetchAPI('/api/DepreciationBatches', {
      method: 'POST',
      body: JSON.stringify(batch),
    });
  }

  async updateDepreciationBatch(id: string, batch: any): Promise<any> {
    if (getDataSource() === 'demo') {
      throw new Error('Cannot update depreciation batch in demo mode. Please switch to API mode.');
    }
    return this.fetchAPI(`/api/DepreciationBatches/${id}`, {
      method: 'PUT',
      body: JSON.stringify(batch),
    });
  }

  async postDepreciationBatch(id: string, postedBy: string): Promise<any> {
    if (getDataSource() === 'demo') {
      throw new Error('Cannot post depreciation batch in demo mode. Please switch to API mode.');
    }
    return this.fetchAPI(`/api/Depreciation/batches/${id}/post?postedBy=${encodeURIComponent(postedBy)}`, {
      method: 'POST',
    });
  }

  async retryDepreciationFinancialsPosting(transactionId: string): Promise<any> {
    if (getDataSource() === 'demo') {
      throw new Error('Cannot retry Financials posting in demo mode. Please switch to API mode.');
    }
    return this.fetchAPI(`/api/Depreciation/transactions/${transactionId}/retry-financials`, {
      method: 'POST',
    });
  }

  /**
   * Get the current period depreciation summary for the batch screen.
   * Returns aggregated data for assets eligible for depreciation in the current period.
   */
  async getCurrentPeriodDepreciationSummary(): Promise<{
    period: string;
    year: number;
    periodNumber: number;
    status: string;
    expectedCompletionBy?: string;
    assetsInScope: number;
    totalCurrentNBV: number;
    totalExpectedNBV: number;
    totalDepreciation: number;
    priorPeriodDepreciation: number;
    exceptionsLastRun: number;
    currency: string;
    isUpToDate: boolean;
    upToDateMessage?: string;
  }> {
    if (getDataSource() === 'demo') {
      // Return mock data for demo mode
      const { mockCurrentPeriod } = await import('@/lib/mock-depreciation-data');
      const { mockAssets } = await import('@/lib/mock-data');
      
      // Calculate totals from mock assets
      const totalCurrentNBV = mockAssets.reduce((sum: number, asset: Asset) => sum + asset.netBookValue, 0);
      const usefulLifeYears = 5;
      const monthlyRate = 1 / (usefulLifeYears * 12);
      const totalDepreciation = mockAssets.reduce((sum: number, asset: Asset) => {
        const depreciation = (asset.netBookValue - (asset.residualValue ?? 0)) * monthlyRate;
        return sum + depreciation;
      }, 0);
      
      return {
        period: mockCurrentPeriod.period,
        year: new Date().getFullYear(),
        periodNumber: new Date().getMonth() + 1,
        status: mockCurrentPeriod.status,
        expectedCompletionBy: mockCurrentPeriod.expectedCompletionBy,
        assetsInScope: mockAssets.length,
        totalCurrentNBV,
        totalExpectedNBV: totalCurrentNBV - totalDepreciation,
        totalDepreciation,
        priorPeriodDepreciation: mockCurrentPeriod.priorPeriodDepreciation,
        exceptionsLastRun: mockCurrentPeriod.exceptionsLastRun,
        currency: 'GBP',
        isUpToDate: false,
        upToDateMessage: undefined,
      };
    }
    return this.fetchAPI('/api/Depreciation/current-period-summary');
  }

  /**
   * Get the list of assets eligible for depreciation in the current period.
   * Returns assets with pre-calculated depreciation values for the period.
   */
  async getCurrentPeriodEligibleAssets(): Promise<Array<{
    id: string;
    assetId: string;
    name: string;
    description?: string;
    category: string;
    location: string;
    status: string;
    acquisitionCost: number;
    accumulatedDepreciation: number;
    netBookValue: number;
    residualValue: number;
    depreciationMethod: string;
    usefulLife?: number;
    postingFrequency?: string;
    calculatedDepreciationValue: number;
    calculatedClosingNBV: number;
    calculatedDepreciationPercent?: number;
    formula?: string;
    formulaBreakdown?: string;
    acquisitionDate?: string;
    inServiceDate?: string;
  }>> {
    if (getDataSource() === 'demo') {
      // Return mock assets transformed to eligible asset format for demo mode
      const { mockAssets } = await import('@/lib/mock-data');
      
      return mockAssets.map((asset: Asset) => {
        const usefulLifeYears = asset.usefulLife ?? 5;
        const monthlyRate = 1 / (usefulLifeYears * 12);
        const depreciationValue = (asset.netBookValue - (asset.residualValue ?? 0)) * monthlyRate;
        
        return {
          id: asset.id,
          assetId: asset.code || asset.id,
          name: asset.name,
          description: asset.description,
          category: asset.category,
          location: asset.location,
          status: asset.status,
          acquisitionCost: asset.acquisitionCost,
          accumulatedDepreciation: asset.accumulatedDepreciation,
          netBookValue: asset.netBookValue,
          residualValue: asset.residualValue ?? 0,
          depreciationMethod: asset.depreciationMethod ?? 'Straight Line',
          usefulLife: usefulLifeYears,
          postingFrequency: 'Monthly',
          calculatedDepreciationValue: depreciationValue,
          calculatedClosingNBV: asset.netBookValue - depreciationValue,
          calculatedDepreciationPercent: (depreciationValue / asset.netBookValue) * 100,
          formula: '(Total Asset Value - Residual Value) / Total Postings',
          formulaBreakdown: 'Depreciation = (' + asset.netBookValue + ' - ' + (asset.residualValue ?? 0) + ') * ' + monthlyRate.toFixed(4),
          acquisitionDate: asset.acquisitionDate,
          inServiceDate: asset.acquisitionDate,
        };
      });
    }
    return this.fetchAPI('/api/Depreciation/current-period-eligible-assets');
  }

  async getRevaluationStatistics(year?: number): Promise<any> {
    if (getDataSource() === 'demo') {
      return {
        year: year || new Date().getFullYear(),
        totalRevaluationAdjustment: 125400.00,
        totalAssetsRevalued: 23,
        totalBatches: 4,
        pendingBatchCount: 1,
        pendingAdjustment: 15200.00
      };
    }

    const queryParams = new URLSearchParams();
    if (year) {
      queryParams.append('year', year.toString());
    }

    const queryString = queryParams.toString();
    return this.fetchAPI(`/api/RevaluationBatches/statistics${queryString ? `?${queryString}` : ''}`);
  }

  async getRevaluationBatches(filters?: { status?: string }): Promise<any[]> {
    if (getDataSource() === 'demo') {
      return [];
    }

    const queryParams = new URLSearchParams();
    if (filters?.status) {
      queryParams.append('status', filters.status);
    }

    const queryString = queryParams.toString();
    return this.fetchAPI(`/api/RevaluationBatches${queryString ? `?${queryString}` : ''}`);
  }

  async getRevaluationBatch(id: string): Promise<any | null> {
    if (getDataSource() === 'demo') {
      return null;
    }
    return this.fetchAPI(`/api/RevaluationBatches/${id}`);
  }
  async createRevaluationBatch(batch: any): Promise<any> {
    if (getDataSource() === 'demo') {
      throw new Error('Cannot create revaluation batch in demo mode. Please switch to API mode.');
    }
    return this.fetchAPI('/api/RevaluationBatches', {
      method: 'POST',
      body: JSON.stringify(batch),
    });
  }

  async updateRevaluationBatch(id: string, batch: any): Promise<any> {
    if (getDataSource() === 'demo') {
      throw new Error('Cannot update revaluation batch in demo mode. Please switch to API mode.');
    }
    return this.fetchAPI(`/api/RevaluationBatches/${id}`, {
      method: 'PUT',
      body: JSON.stringify(batch),
    });
  }

  async postRevaluationBatch(id: string, postedBy: string): Promise<any> {
    if (getDataSource() === 'demo') {
      throw new Error('Cannot post revaluation batch in demo mode. Please switch to API mode.');
    }
    return this.fetchAPI(`/api/RevaluationBatches/${id}/post?postedBy=${encodeURIComponent(postedBy)}`, {
      method: 'POST',
    });
  }

  async retryRevaluationFinancialsPosting(transactionId: string): Promise<any> {
    if (getDataSource() === 'demo') {
      throw new Error('Cannot retry Financials posting in demo mode. Please switch to API mode.');
    }
    return this.fetchAPI(`/api/RevaluationBatches/transactions/${transactionId}/retry-financials`, {
      method: 'POST',
    });
  }

  async deleteRevaluationBatch(id: string): Promise<void> {
    if (getDataSource() === 'demo') {
      throw new Error('Cannot delete revaluation batch in demo mode. Please switch to API mode.');
    }
    await this.fetchAPI(`/api/RevaluationBatches/${id}`, { method: 'DELETE' });
  }


  async getLocations(): Promise<Location[]> {
    if (getDataSource() === 'demo') {
      return mockLocations;
    }
    const apiLocations = await this.fetchAPI('/api/Settings/locations');
    return apiLocations;
  }

  async getLocation(id: string): Promise<Location> {
    if (getDataSource() === 'demo') {
      const location = mockLocations.find(l => l.id === id);
      if (!location) throw new Error('Location not found');
      return location;
    }
    const loc = await this.fetchAPI(`/api/Settings/locations/${id}`);
    return loc;
  }

  async createLocation(location: Partial<Location>): Promise<Location> {
    if (getDataSource() === 'demo') {
      throw new Error('Cannot create locations in demo mode. Please switch to API mode.');
    }
    return this.fetchAPI('/api/Settings/locations', {
      method: 'POST',
      body: JSON.stringify(location),
    });
  }

  async updateLocation(id: string, location: Partial<Location>): Promise<Location> {
    if (getDataSource() === 'demo') {
      throw new Error('Cannot update locations in demo mode. Please switch to API mode.');
    }
    return this.fetchAPI(`/api/Settings/locations/${id}`, {
      method: 'PUT',
      body: JSON.stringify(location),
    });
  }

  async deleteLocation(id: string): Promise<void> {
    if (getDataSource() === 'demo') {
      throw new Error('Cannot delete locations in demo mode. Please switch to API mode.');
    }
    await this.fetchAPI(`/api/Settings/locations/${id}`, { method: 'DELETE' });
  }

  async getSubLocationsByParentId(parentLocationId: string): Promise<import('@/types/settings').SubLocation[]> {
    if (getDataSource() === 'demo') {
      return mockSubLocations.filter(sl => sl.parentLocationId === parentLocationId);
    }
    return this.fetchAPI(`/api/Settings/locations/${parentLocationId}/sublocations`);
  }

  async getCategories(): Promise<Category[]> {
    if (getDataSource() === 'demo') {
      return mockCategories;
    }
    const apiCategories = await this.fetchAPI('/api/Settings/categories');
    return apiCategories;
  }

  async getCategory(id: string): Promise<Category> {
    if (getDataSource() === 'demo') {
      const category = mockCategories.find(c => c.id === id);
      if (!category) throw new Error('Category not found');
      return category;
    }
    return await this.fetchAPI(`/api/Settings/categories/${id}`);
  }

  async createCategory(category: Partial<Category>): Promise<Category> {
    if (getDataSource() === 'demo') {
      throw new Error('Cannot create categories in demo mode. Please switch to API mode.');
    }
    const cat = await this.fetchAPI('/api/Settings/categories', {
      method: 'POST',
      body: JSON.stringify(category),
    });
    return cat;
  }

  async updateCategory(id: string, category: Partial<Category>): Promise<Category> {
    if (getDataSource() === 'demo') {
      throw new Error('Cannot update categories in demo mode. Please switch to API mode.');
    }
    const cat = await this.fetchAPI(`/api/Settings/categories/${id}`, {
      method: 'PUT',
      body: JSON.stringify(category),
    });
    return cat;
  }

  async deleteCategory(id: string): Promise<void> {
    const dataSource = getDataSource();
    if (dataSource === 'demo') {
      throw new Error('Cannot delete categories in demo mode. Please switch to API mode.');
    }
    await this.fetchAPI(`/api/Settings/categories/${id}`, { method: 'DELETE' });
  }

  async getSubCategoriesByParentId(parentCategoryId: string): Promise<SubCategory[]> {
    if (getDataSource() === 'demo') {
      return mockSubCategories.filter(sc => sc.parentCategoryId === parentCategoryId);
    }
    return this.fetchAPI(`/api/Settings/categories/${parentCategoryId}/subcategories`);
  }

  async getSubCategory(id: string): Promise<SubCategory> {
    if (getDataSource() === 'demo') {
      const subCategory = mockSubCategories.find(sc => sc.id === id);
      if (!subCategory) throw new Error('Sub-category not found');
      return subCategory;
    }
    return this.fetchAPI(`/api/Settings/subcategories/${id}`);
  }

  async createSubCategory(parentCategoryId: string, subCategory: Partial<SubCategory>): Promise<SubCategory> {
    if (getDataSource() === 'demo') {
      throw new Error('Cannot create sub-categories in demo mode. Please switch to API mode.');
    }
    return this.fetchAPI(`/api/Settings/categories/${parentCategoryId}/subcategories`, {
      method: 'POST',
      body: JSON.stringify(subCategory),
    });
  }

  async updateSubCategory(id: string, subCategory: Partial<SubCategory>): Promise<SubCategory> {
    if (getDataSource() === 'demo') {
      throw new Error('Cannot update sub-categories in demo mode. Please switch to API mode.');
    }
    return this.fetchAPI(`/api/Settings/subcategories/${id}`, {
      method: 'PUT',
      body: JSON.stringify(subCategory),
    });
  }

  async deleteSubCategory(id: string): Promise<void> {
    if (getDataSource() === 'demo') {
      throw new Error('Cannot delete sub-categories in demo mode. Please switch to API mode.');
    }
    await this.fetchAPI(`/api/Settings/subcategories/${id}`, { method: 'DELETE' });
  }

  async getCategoryDefaults(categoryId: string): Promise<import('@/types/settings').CategoryDefaults> {
    if (getDataSource() === 'demo') {
      // Return mock defaults based on category
      return {
        method: 'Straight Line',
        percentage: 20.0,
        years: 5,
        frequency: 'Monthly',
        nominalAccounts: {
          assetCost: { code: '1200', name: 'Fixed Assets' },
          depreciationBS: { code: '1210', name: 'Accumulated Depreciation' },
          depreciationPL: { code: '6100', name: 'Depreciation Expense' },
          disposals: { code: '5100', name: 'Disposal of Assets' },
        },
      };
    }
    return this.fetchAPI(`/api/Settings/categories/${categoryId}/defaults`);
  }

  /**
   * Transform backend EquipmentDto to frontend Equipment type.
   * Maps BoughtValue (backend) to acquisitionValue (frontend)
   * Maps BoughtDate (backend) to acquiredDate (frontend)
   */
  private transformEquipmentFromDto(dto: Record<string, unknown>): Equipment {
    return {
      id: dto.id as string,
      code: dto.code as string,
      description: dto.description as string | undefined,
      acquisitionValue: (dto.boughtValue as number) ?? 0,
      acquiredDate: dto.boughtDate as string | undefined,
      linkedAssetId: dto.linkedAssetId as string | undefined,
      locationId: dto.locationId as string | undefined,
      notes: dto.notes as string | undefined,
      createdBy: dto.createdBy as string,
      createdDate: dto.createdDate as string,
      modifiedBy: dto.modifiedBy as string | undefined,
      modifiedDate: dto.modifiedDate as string | undefined,
    };
  }

  /**
   * Transform frontend Equipment to backend EquipmentDto format.
   * Maps acquisitionValue (frontend) to BoughtValue (backend)
   * Maps acquiredDate (frontend) to BoughtDate (backend)
   */
  private transformEquipmentToDto(equipment: Partial<Equipment>): Record<string, unknown> {
    return {
      id: equipment.id,
      code: equipment.code,
      description: equipment.description,
      boughtValue: equipment.acquisitionValue ?? 0,
      boughtDate: equipment.acquiredDate,
      linkedAssetId: equipment.linkedAssetId,
      locationId: equipment.locationId,
      notes: equipment.notes,
      createdBy: equipment.createdBy,
      createdDate: equipment.createdDate,
      modifiedBy: equipment.modifiedBy,
      modifiedDate: equipment.modifiedDate,
    };
  }

  async getEquipment(): Promise<Equipment[]> {
    if (getDataSource() === 'demo') {
      return mockEquipment;
    }
    const apiEquipment = await this.fetchAPI('/api/Settings/equipment');
    return (apiEquipment as Record<string, unknown>[]).map(dto => this.transformEquipmentFromDto(dto));
  }

  async getEquipmentItem(id: string): Promise<Equipment> {
    if (getDataSource() === 'demo') {
      const equipment = mockEquipment.find(e => e.id === id);
      if (!equipment) throw new Error('Equipment not found');
      return equipment;
    }
    const dto = await this.fetchAPI(`/api/Settings/equipment/${id}`);
    return this.transformEquipmentFromDto(dto as Record<string, unknown>);
  }

  async createEquipment(equipment: Partial<Equipment>): Promise<Equipment> {
    if (getDataSource() === 'demo') {
      throw new Error('Cannot create equipment in demo mode. Please switch to API mode.');
    }
    const dto = this.transformEquipmentToDto(equipment);
    const response = await this.fetchAPI('/api/Settings/equipment', {
      method: 'POST',
      body: JSON.stringify(dto),
    });
    return this.transformEquipmentFromDto(response as Record<string, unknown>);
  }

  async updateEquipment(id: string, equipment: Partial<Equipment>): Promise<Equipment> {
    if (getDataSource() === 'demo') {
      throw new Error('Cannot update equipment in demo mode. Please switch to API mode.');
    }
    const dto = this.transformEquipmentToDto(equipment);
    const response = await this.fetchAPI(`/api/Settings/equipment/${id}`, {
      method: 'PUT',
      body: JSON.stringify(dto),
    });
    return this.transformEquipmentFromDto(response as Record<string, unknown>);
  }

  async deleteEquipment(id: string): Promise<void> {
    if (getDataSource() === 'demo') {
      throw new Error('Cannot delete equipment in demo mode. Please switch to API mode.');
    }
    await this.fetchAPI(`/api/Settings/equipment/${id}`, { method: 'DELETE' });
  }

  async getDisposalReasons(): Promise<DisposalReason[]> {
    if (getDataSource() === 'demo') {
      return mockDisposalReasons;
    }
    const apiReasons = await this.fetchAPI('/api/Settings/disposal-reasons');
    return apiReasons;
  }

  async getDisposalReason(id: string): Promise<DisposalReason> {
    if (getDataSource() === 'demo') {
      const reason = mockDisposalReasons.find(r => r.id === id);
      if (!reason) throw new Error('Disposal reason not found');
      return reason;
    }
    return this.fetchAPI(`/api/Settings/disposal-reasons/${id}`);
  }

  async createDisposalReason(reason: Partial<DisposalReason>): Promise<DisposalReason> {
    if (getDataSource() === 'demo') {
      throw new Error('Cannot create disposal reasons in demo mode. Please switch to API mode.');
    }
    return this.fetchAPI('/api/Settings/disposal-reasons', {
      method: 'POST',
      body: JSON.stringify(reason),
    });
  }

  async updateDisposalReason(id: string, reason: Partial<DisposalReason>): Promise<DisposalReason> {
    if (getDataSource() === 'demo') {
      throw new Error('Cannot update disposal reasons in demo mode. Please switch to API mode.');
    }
    return this.fetchAPI(`/api/Settings/disposal-reasons/${id}`, {
      method: 'PUT',
      body: JSON.stringify(reason),
    });
  }

  async deleteDisposalReason(id: string): Promise<void> {
    if (getDataSource() === 'demo') {
      throw new Error('Cannot delete disposal reasons in demo mode. Please switch to API mode.');
    }
    await this.fetchAPI(`/api/Settings/disposal-reasons/${id}`, { method: 'DELETE' });
  }

  // Addition Batch methods
  async getAdditionBatches(status?: string): Promise<any[]> {
    if (getDataSource() === 'demo') {
      return [];
    }
    const endpoint = status ? `/api/AdditionBatches?status=${status}` : '/api/AdditionBatches';
    return this.fetchAPI(endpoint);
  }

  async getAdditionBatch(id: string): Promise<any> {
    if (getDataSource() === 'demo') {
      throw new Error('Cannot get addition batch in demo mode. Please switch to API mode.');
    }
    return this.fetchAPI(`/api/AdditionBatches/${id}`);
  }

  async createAdditionBatch(batch: any): Promise<any> {
    if (getDataSource() === 'demo') {
      throw new Error('Cannot create addition batch in demo mode. Please switch to API mode.');
    }
    return this.fetchAPI('/api/AdditionBatches', {
      method: 'POST',
      body: JSON.stringify(batch),
    });
  }

  async updateAdditionBatch(id: string, batch: any): Promise<any> {
    if (getDataSource() === 'demo') {
      throw new Error('Cannot update addition batch in demo mode. Please switch to API mode.');
    }
    return this.fetchAPI(`/api/AdditionBatches/${id}`, {
      method: 'PUT',
      body: JSON.stringify(batch),
    });
  }

  async postAdditionBatch(id: string, postedBy: string): Promise<any> {
    if (getDataSource() === 'demo') {
      throw new Error('Cannot post addition batch in demo mode. Please switch to API mode.');
    }
    return this.fetchAPI(`/api/AdditionBatches/${id}/post?postedBy=${encodeURIComponent(postedBy)}`, {
      method: 'POST',
    });
  }

  async retryAdditionFinancialsPosting(transactionId: string): Promise<any> {
    if (getDataSource() === 'demo') {
      throw new Error('Cannot retry Financials posting in demo mode. Please switch to API mode.');
    }
    return this.fetchAPI(`/api/AdditionBatches/transactions/${transactionId}/retry-financials`, {
      method: 'POST',
    });
  }

  async deleteAdditionBatch(id: string): Promise<void> {
    if (getDataSource() === 'demo') {
      throw new Error('Cannot delete addition batch in demo mode. Please switch to API mode.');
    }
    await this.fetchAPI(`/api/AdditionBatches/${id}`, { method: 'DELETE' });
  }

  // Disposal methods
  async getDisposalPreview(request: DisposalPreviewRequest): Promise<DisposalPreviewResponse> {
    if (getDataSource() === 'demo') {
      // Return mock preview data for demo mode
      const asset = mockAssets.find(a => a.id === request.assetId);
      if (!asset) {
        throw new Error('Asset not found');
      }

      // Calculate disposal values based on mode
      const currentYear = new Date().getFullYear();
      const currentPeriod = new Date().getMonth() + 1;
      const currentQuantity = asset.quantity ?? 1;

      if (request.disposeOfMode === 'percentage') {
        return {
          year: currentYear,
          period: currentPeriod,
          currentQuantity,
          hasCurrentPeriodDepreciation: false,
          actualCost: 0,
          actualDepreciation: 0,
          historicalCost: 0,
          historicalDepreciation: 0,
          actualCostPercentage: 100,
          actualDepreciationPercentage: 100,
          historicalCostPercentage: 100,
          historicalDepreciationPercentage: 100,
          proceedsValue: 0,
        };
      } else if (request.disposeOfMode === 'quantity' && request.disposalQuantity) {
        const ratio = request.disposalQuantity / currentQuantity;
        return {
          year: currentYear,
          period: currentPeriod,
          currentQuantity,
          hasCurrentPeriodDepreciation: false,
          actualCost: asset.acquisitionCost * ratio,
          actualDepreciation: asset.accumulatedDepreciation * ratio,
          historicalCost: asset.acquisitionCost * ratio,
          historicalDepreciation: asset.accumulatedDepreciation * ratio,
          actualCostPercentage: 100,
          actualDepreciationPercentage: 100,
          historicalCostPercentage: 100,
          historicalDepreciationPercentage: 100,
          proceedsValue: 0,
        };
      } else {
        // Value mode - dispose of full asset
        return {
          year: currentYear,
          period: currentPeriod,
          currentQuantity,
          hasCurrentPeriodDepreciation: false,
          actualCost: asset.acquisitionCost,
          actualDepreciation: asset.accumulatedDepreciation,
          historicalCost: asset.acquisitionCost,
          historicalDepreciation: asset.accumulatedDepreciation,
          actualCostPercentage: 100,
          actualDepreciationPercentage: 100,
          historicalCostPercentage: 100,
          historicalDepreciationPercentage: 100,
          proceedsValue: 0,
        };
      }
    }
    return this.fetchAPI('/api/Disposal/preview', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  async submitDisposal(request: DisposalSubmitRequest): Promise<DisposalSubmitResponse> {
    if (getDataSource() === 'demo') {
      throw new Error('Cannot submit disposal in demo mode. Please switch to API mode.');
    }
    return this.fetchAPI('/api/Disposal/submit', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  async submitMassDisposal(request: any): Promise<any> {
    if (getDataSource() === 'demo') {
      throw new Error('Cannot submit mass disposal in demo mode. Please switch to API mode.');
    }
    return this.fetchAPI('/api/Disposal/mass', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  async checkDisposalEligibility(request: { assetId: string }): Promise<any> {
    if (getDataSource() === 'demo') {
      return {
        isEligible: true,
        hasCurrentPeriodDepreciation: false,
        assetStatus: 'Active',
      };
    }
    return this.fetchAPI('/api/Disposal/eligibility', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  async getDisposalTransactions(query?: {
    page?: number;
    pageSize?: number;
    year?: string;
    period?: string;
    sortBy?: string;
    order?: string;
  }): Promise<any> {
    const params = new URLSearchParams();
    params.append('type', 'Disposal');
    if (query?.page) params.append('page', query.page.toString());
    if (query?.pageSize) params.append('pageSize', query.pageSize.toString());
    if (query?.year) params.append('year', query.year);
    if (query?.period) params.append('period', query.period);
    if (query?.sortBy) params.append('sortBy', query.sortBy);
    if (query?.order) params.append('order', query.order);
    
    return this.fetchAPI(`/api/Transactions?${params.toString()}`);
  }

  // Audit methods
  async getAuditLogs(filter: any): Promise<any> {
    if (getDataSource() === 'demo') {
      // Map AuditEntry to AuditLog shape for demo mode
      // mockAuditLog uses 'action' field but AuditLog type expects 'actionType'
      const page = filter.page ?? 1;
      const pageSize = filter.pageSize ?? 50;
      const start = (page - 1) * pageSize;
      const end = start + pageSize;

      const slice = mockAuditLog.slice(start, end);

      const items = slice.map(entry => {
                const asset = mockAssets.find(a => a.id === entry.assetId);
                return {
                  id: entry.id,
                  assetId: entry.assetId ?? '',
                  assetCode: asset?.code ?? '',  // user-facing asset code
          assetName: asset?.name ?? '',
          timestamp: entry.timestamp,
          user: entry.user,
          actionType: entry.action,  // Map 'action' to 'actionType'
          field: entry.field,
          oldValue: entry.oldValue,
          newValue: entry.newValue,
          description: entry.notes,
        };
      });

      return {
        items,
        totalCount: mockAuditLog.length,
        page,
        pageSize,
        totalPages: Math.ceil(mockAuditLog.length / pageSize)
      };
    }

    const params = new URLSearchParams();
    if (filter.assetCode) params.append('assetCode', filter.assetCode);
    if (filter.assetDescription) params.append('assetDescription', filter.assetDescription);
    if (filter.startDate) params.append('startDate', filter.startDate);
    if (filter.endDate) params.append('endDate', filter.endDate);
    if (filter.actionType) params.append('actionType', filter.actionType);
    if (filter.user) params.append('user', filter.user);
    if (filter.page) params.append('page', filter.page.toString());
    if (filter.pageSize) params.append('pageSize', filter.pageSize.toString());
    if (filter.sortBy) params.append('sortBy', filter.sortBy);
    if (filter.sortOrder) params.append('sortOrder', filter.sortOrder);

    return this.fetchAPI(`/api/Audit?${params.toString()}`);
  }

  async getAuditLog(id: string): Promise<any> {
    if (getDataSource() === 'demo') {
      return mockAuditLog.find(log => log.id === id);
    }
    return this.fetchAPI(`/api/Audit/${id}`);
  }

  async getAuditActionTypes(): Promise<string[]> {
    if (getDataSource() === 'demo') {
      return ['Create', 'Edit', 'Delete', 'Move', 'StatusChange', 'ValueChange', 'Disposal', 'Transfer'];
    }
    return this.fetchAPI('/api/Audit/action-types');
  }

  async getAuditUsers(): Promise<string[]> {
    if (getDataSource() === 'demo') {
      return ['Jasmine Williams', 'John Smith', 'Sarah Johnson'];
    }
    return this.fetchAPI('/api/Audit/users');
  }

  async exportAuditLogs(filter: any, format: 'csv' | 'pdf'): Promise<void> {
    if (getDataSource() === 'demo') {
      throw new Error('Cannot export audit logs in demo mode. Please switch to API mode.');
    }

    const apiBaseUrl = this.getApiBaseUrl();
    const endpoint = format === 'csv' ? '/api/Audit/export/csv' : '/api/Audit/export/pdf';

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (this.authHeader) {
      headers['Authorization'] = this.authHeader;
    }

    const authConfig = getAuthConfig();

    if (authConfig.getAccessToken) {
      try {
        const token = await authConfig.getAccessToken();
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      } catch (error) {
        console.error('Error getting access token:', error);
      }
    }

    const instanceId = authConfig.instanceId ?? 'default-instance';
    headers['X-Instance-Id'] = instanceId;

    const response = await fetch(`${apiBaseUrl}${endpoint}`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        assetId: filter.assetId,
        startDate: filter.startDate,
        endDate: filter.endDate,
        actionType: filter.actionType,
        user: filter.user,
        format
      }),
    });

    if (!response.ok) {
      throw new Error(`Export failed: ${response.statusText}`);
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-trail-${new Date().toISOString().split('T')[0]}.${format === 'csv' ? 'csv' : 'html'}`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }

  async emailAuditTrail(request: any): Promise<any> {
    if (getDataSource() === 'demo') {
      throw new Error('Cannot email audit trail in demo mode. Please switch to API mode.');
    }
    return this.fetchAPI('/api/Audit/email', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  async getFinancialsDatabaseInstances(integrationType: string): Promise<{
    instances: Array<{
      id: string;
      name: string;
      environment?: string;
      connectionStatus: string;
      lastSync?: string;
      description?: string;
    }>;
    integrationType: string;
  }> {
    return this.fetchAPI(`/api/financials/instances`);
  }

  async getDimensionsDatabaseInstances(): Promise<{
    instances: Array<{
      id: string;
      name: string;
      environment?: string;
      connectionStatus: string;
      lastSync?: string;
      description?: string;
    }>;
    integrationType: string;
  }> {
    return this.fetchAPI(`/api/dimensions/instances`);
  }

  async getIntegrationSettings(): Promise<{
    id: string;
    integrationType: string;
    instanceId?: string;
    instanceName?: string;
    enabled: boolean;
    lastSync?: string;
  } | null> {
    try {
      const financialsSettings = await this.fetchAPI(`/api/financials/settings`);
      if (financialsSettings?.enabled) {
        return financialsSettings;
      }
    } catch {
      // Ignore errors for financials settings
    }

    try {
      const dimensionsSettings = await this.fetchAPI(`/api/dimensions/settings`);
      if (dimensionsSettings?.enabled) {
        return dimensionsSettings;
      }
    } catch {
      // Ignore errors for dimensions settings
    }

    return null;
  }

  async saveIntegrationSettings(settings: {
    integrationType: string;
    instanceId?: string;
    instanceName?: string;
    enabled: boolean;
  }): Promise<{
    id: string;
    integrationType: string;
    instanceId?: string;
    instanceName?: string;
    enabled: boolean;
    lastSync?: string;
  }> {
    const endpoint = settings.integrationType === 'access-dimensions'
      ? '/api/dimensions/settings'
      : '/api/financials/settings';
    return this.fetchAPI(endpoint, {
      method: 'POST',
      body: JSON.stringify(settings),
    });
  }

  // Financial Operations
  async hasTransactions(assetId: string): Promise<boolean> {
    if (getDataSource() === 'demo') {
      // In demo mode, return false for all assets
      return false;
    }
    const response = await this.fetchAPI(`/api/Assets/${assetId}/has-transactions`);
    return response.hasTransactions;
  }

  async generateAssetCode(): Promise<string> {
    if (getDataSource() === 'demo') {
      // In demo mode, generate a simple code
      const timestamp = Date.now();
      return `AST${(timestamp % 1000000).toString().padStart(6, '0')}`;
    }
    const response = await this.fetchAPI('/api/Assets/generate-code', {
      method: 'POST',
    });
    return response.code;
  }

  async getAssetYearValues(assetId: string): Promise<any[]> {
    if (getDataSource() === 'demo') {
      // Use extracted helper function to reduce cognitive complexity (S3776)
      return generateMockYearValues();
    }
    return this.fetchAPI(`/api/Assets/${assetId}/year-values`);
  }

  // System Options
  async getSystemOptions(): Promise<any> {
    if (getDataSource() === 'demo') {
      // Return default system options for demo mode
      return {
        configType: 'STANDARD',
        isMultiEntity: false,
        autoNumbering: {
          enabled: false,
          numberOfChars: 10,
          currentNumber: 1,
        },
        validation: {
          noNegativeBoughtValue: false,
        },
        defaults: {
          baseValueOn: 'remaining',
          servicePeriod: 0,
          forceTransfersOnChangingCategory: false,
        },
      };
    }
    return this.fetchAPI('/api/Settings/system-options');
  }

  async updateSystemOptions(options: any): Promise<any> {
    if (getDataSource() === 'demo') {
      throw new Error('Cannot update system options in demo mode. Please switch to API mode.');
    }
    return this.fetchAPI('/api/Settings/system-options', {
      method: 'PUT',
      body: JSON.stringify(options),
    });
  }

  async getLanguageSettings(): Promise<{
    settings: Array<{
      id: string;
      fieldKey: string;
      pageContext: string;
      defaultLabel: string;
      customLabel: string | null;
      createdBy?: string;
      createdDate: string;
      modifiedBy?: string;
      modifiedDate?: string;
    }>;
  }> {
    if (getDataSource() === 'demo') {
      return {
        settings: [
          { id: '1', fieldKey: 'SubLocation', pageContext: 'General', defaultLabel: 'Sub Location', customLabel: null, createdDate: new Date().toISOString() },
          { id: '2', fieldKey: 'SubCategory', pageContext: 'General', defaultLabel: 'Sub Category', customLabel: null, createdDate: new Date().toISOString() },
          { id: '3', fieldKey: 'UserKey1', pageContext: 'AssetCustomFields', defaultLabel: 'User Key 1', customLabel: null, createdDate: new Date().toISOString() },
          { id: '4', fieldKey: 'UserKey2', pageContext: 'AssetCustomFields', defaultLabel: 'User Key 2', customLabel: null, createdDate: new Date().toISOString() },
          { id: '5', fieldKey: 'UserKey3', pageContext: 'AssetCustomFields', defaultLabel: 'User Key 3', customLabel: null, createdDate: new Date().toISOString() },
          { id: '6', fieldKey: 'UserKey4', pageContext: 'AssetCustomFields', defaultLabel: 'User Key 4', customLabel: null, createdDate: new Date().toISOString() },
          { id: '7', fieldKey: 'UserKey5', pageContext: 'AssetCustomFields', defaultLabel: 'User Key 5', customLabel: null, createdDate: new Date().toISOString() },
          { id: '8', fieldKey: 'UserValue1', pageContext: 'AssetCustomFields', defaultLabel: 'User Value 1', customLabel: null, createdDate: new Date().toISOString() },
          { id: '9', fieldKey: 'UserValue2', pageContext: 'AssetCustomFields', defaultLabel: 'User Value 2', customLabel: null, createdDate: new Date().toISOString() },
          { id: '10', fieldKey: 'UserValue3', pageContext: 'AssetCustomFields', defaultLabel: 'User Value 3', customLabel: null, createdDate: new Date().toISOString() },
          { id: '11', fieldKey: 'UserValue4', pageContext: 'AssetCustomFields', defaultLabel: 'User Value 4', customLabel: null, createdDate: new Date().toISOString() },
          { id: '12', fieldKey: 'UserValue5', pageContext: 'AssetCustomFields', defaultLabel: 'User Value 5', customLabel: null, createdDate: new Date().toISOString() },
          { id: '13', fieldKey: 'UserDate1', pageContext: 'AssetCustomFields', defaultLabel: 'User Date 1', customLabel: null, createdDate: new Date().toISOString() },
          { id: '14', fieldKey: 'UserDate2', pageContext: 'AssetCustomFields', defaultLabel: 'User Date 2', customLabel: null, createdDate: new Date().toISOString() },
          { id: '15', fieldKey: 'UserDate3', pageContext: 'AssetCustomFields', defaultLabel: 'User Date 3', customLabel: null, createdDate: new Date().toISOString() },
          { id: '16', fieldKey: 'EquipmentText1', pageContext: 'EquipmentCustomFields', defaultLabel: 'Text 1', customLabel: null, createdDate: new Date().toISOString() },
          { id: '17', fieldKey: 'EquipmentText2', pageContext: 'EquipmentCustomFields', defaultLabel: 'Text 2', customLabel: null, createdDate: new Date().toISOString() },
          { id: '18', fieldKey: 'EquipmentText3', pageContext: 'EquipmentCustomFields', defaultLabel: 'Text 3', customLabel: null, createdDate: new Date().toISOString() },
          { id: '19', fieldKey: 'EquipmentValue1', pageContext: 'EquipmentCustomFields', defaultLabel: 'Value 1', customLabel: null, createdDate: new Date().toISOString() },
          { id: '20', fieldKey: 'EquipmentValue2', pageContext: 'EquipmentCustomFields', defaultLabel: 'Value 2', customLabel: null, createdDate: new Date().toISOString() },
          { id: '21', fieldKey: 'EquipmentValue3', pageContext: 'EquipmentCustomFields', defaultLabel: 'Value 3', customLabel: null, createdDate: new Date().toISOString() },
          { id: '22', fieldKey: 'EquipmentDate1', pageContext: 'EquipmentCustomFields', defaultLabel: 'Date 1', customLabel: null, createdDate: new Date().toISOString() },
          { id: '23', fieldKey: 'EquipmentDate2', pageContext: 'EquipmentCustomFields', defaultLabel: 'Date 2', customLabel: null, createdDate: new Date().toISOString() },
          { id: '24', fieldKey: 'EquipmentDate3', pageContext: 'EquipmentCustomFields', defaultLabel: 'Date 3', customLabel: null, createdDate: new Date().toISOString() },
          { id: '25', fieldKey: 'EquipmentOption1', pageContext: 'EquipmentCustomFields', defaultLabel: 'Option 1', customLabel: null, createdDate: new Date().toISOString() },
          { id: '26', fieldKey: 'EquipmentOption2', pageContext: 'EquipmentCustomFields', defaultLabel: 'Option 2', customLabel: null, createdDate: new Date().toISOString() },
          { id: '27', fieldKey: 'EquipmentOption3', pageContext: 'EquipmentCustomFields', defaultLabel: 'Option 3', customLabel: null, createdDate: new Date().toISOString() },
        ],
      };
    }
    return this.fetchAPI('/api/Settings/language-settings');
  }

  async updateLanguageSettings(settings: Array<{
    fieldKey: string;
    customLabel: string | null;
  }>): Promise<{
    settings: Array<{
      id: string;
      fieldKey: string;
      pageContext: string;
      defaultLabel: string;
      customLabel: string | null;
      createdBy?: string;
      createdDate: string;
      modifiedBy?: string;
      modifiedDate?: string;
    }>;
  }> {
    if (getDataSource() === 'demo') {
      throw new Error('Cannot update language settings in demo mode. Please switch to API mode.');
    }
    return this.fetchAPI('/api/Settings/language-settings', {
      method: 'PUT',
      body: JSON.stringify({ settings }),
    });
  }

  async getFeatureSettings(): Promise<{
    modules: Record<string, any>;
    lastUpdated?: string;
    version?: string;
  }> {
    return this.fetchAPI('/api/Settings/features');
  }

  async saveFeatureSettings(settings: {
    modules: Record<string, any>;
    lastUpdated: string;
    version: string;
  }): Promise<{
    modules: Record<string, any>;
    lastUpdated?: string;
    version?: string;
  }> {
    return this.fetchAPI('/api/Settings/features', {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
  }

  async getSubLedgers(type?: string): Promise<any[]> {
    if (getDataSource() === 'demo') {
      const mockSubLedgers = [
        { id: '1', code: 'SL001', description: 'Main Sub-Ledger', type: 'N' },
        { id: '2', code: 'SL002', description: 'Secondary Sub-Ledger', type: 'N' },
      ];
      if (type) {
        return mockSubLedgers.filter(sl => sl.type === type);
      }
      return mockSubLedgers;
    }
    const params = type ? `?type=${type}` : '';
    return this.fetchAPI(`/api/Settings/sub-ledgers${params}`);
  }

  async getNominalAccounts(currencyCode?: string, _multiEntity?: boolean): Promise<any[]> {
    // Use the integration service registry to get the appropriate service
    // based on the current integration type (Financials, Dimensions, etc.)
    const integrationService = getIntegrationService(null);
    return integrationService.getNominalAccounts(currencyCode);
  }

  async getSuppliers(includeInactive?: boolean): Promise<any[]> {
    if (getDataSource() === 'demo') {
      const mockSuppliers = [
        { primaryKey: 1, code: 'SUP001', name: 'Acme Corporation', inactive: false, balance: 15000.00, currencyCode: 'GBP' },
        { primaryKey: 2, code: 'SUP002', name: 'Tech Solutions Ltd', inactive: false, balance: 8500.50, currencyCode: 'GBP' },
        { primaryKey: 3, code: 'SUP003', name: 'Office Supplies Inc', inactive: false, balance: 2300.00, currencyCode: 'GBP' },
        { primaryKey: 4, code: 'SUP004', name: 'Industrial Equipment Co', inactive: false, balance: 45000.00, currencyCode: 'GBP' },
        { primaryKey: 5, code: 'SUP005', name: 'Global Logistics', inactive: true, balance: 0, currencyCode: 'GBP' },
        { primaryKey: 6, code: 'SUP006', name: 'Premium Parts Ltd', inactive: false, balance: 12750.25, currencyCode: 'GBP' },
      ];
      if (includeInactive) {
        return mockSuppliers;
      }
      return mockSuppliers.filter(s => !s.inactive);
    }
    const params = includeInactive ? '?includeInactive=true' : '';
    return this.fetchAPI(`/api/suppliers${params}`);
  }

  async getSystemYears(sort?: string): Promise<any[]> {
    if (getDataSource() === 'demo') {
      const currentYear = new Date().getFullYear();
      const years = [
        {
          id: '1',
          label: `${currentYear - 1}`,
          startDate: new Date(currentYear - 1, 0, 1).toISOString(),
          endDate: new Date(currentYear - 1, 11, 31).toISOString(),
          nominal: currentYear - 1,
        },
        {
          id: '2',
          label: `${currentYear}`,
          startDate: new Date(currentYear, 0, 1).toISOString(),
          endDate: new Date(currentYear, 11, 31).toISOString(),
          nominal: currentYear,
        },
        {
          id: '3',
          label: `${currentYear + 1}`,
          startDate: new Date(currentYear + 1, 0, 1).toISOString(),
          endDate: new Date(currentYear + 1, 11, 31).toISOString(),
          nominal: currentYear + 1,
        },
      ];
      if (sort === 'desc') {
        return years.reverse();
      }
      return years;
    }
    const params = sort ? `?sort=${sort}` : '';
    return this.fetchAPI(`/api/Settings/system-years${params}`);
  }

  async getAssetLookupText(origin: string): Promise<any[]> {
    if (getDataSource() === 'demo') {
      const mockData: Record<string, any[]> = {
        ASSORT1: [
          { id: '1', origin: 'ASSORT1', text: 'Type A', sortOrder: 1 },
          { id: '2', origin: 'ASSORT1', text: 'Type B', sortOrder: 2 },
          { id: '3', origin: 'ASSORT1', text: 'Type C', sortOrder: 3 },
        ],
        ASSORT2: [
          { id: '4', origin: 'ASSORT2', text: 'Priority High', sortOrder: 1 },
          { id: '5', origin: 'ASSORT2', text: 'Priority Medium', sortOrder: 2 },
          { id: '6', origin: 'ASSORT2', text: 'Priority Low', sortOrder: 3 },
        ],
        ASSORT3: [
          { id: '7', origin: 'ASSORT3', text: 'Department A', sortOrder: 1 },
          { id: '8', origin: 'ASSORT3', text: 'Department B', sortOrder: 2 },
        ],
        ASSORT4: [
          { id: '9', origin: 'ASSORT4', text: 'Status Active', sortOrder: 1 },
          { id: '10', origin: 'ASSORT4', text: 'Status Inactive', sortOrder: 2 },
        ],
        ASSORT5: [
          { id: '11', origin: 'ASSORT5', text: 'Region North', sortOrder: 1 },
          { id: '12', origin: 'ASSORT5', text: 'Region South', sortOrder: 2 },
          { id: '13', origin: 'ASSORT5', text: 'Region East', sortOrder: 3 },
          { id: '14', origin: 'ASSORT5', text: 'Region West', sortOrder: 4 },
        ],
      };
      return mockData[origin.toUpperCase()] ?? [];
    }
    return this.fetchAPI(`/api/Settings/asset-lookup-text?origin=${origin}`);
  }

  // Asset Options - fetched from backend API with localStorage fallback
  async getAssetOptions(): Promise<any> {
    // Helper function to get localStorage options
    const getLocalStorageOptions = () => {
      const savedOptionsStr = localStorage.getItem('assetOptions');
      if (savedOptionsStr) {
        return JSON.parse(savedOptionsStr);
      }
      return null;
    };

    // Helper function to get default options
    const getDefaultOptions = () => ({
      general: {
        assetAutoNumbering: false,
        doNotAllowNegativeBoughtValue: false,
        defaultBaseValueOn: 'remaining',
        servicePeriodDefaults: 0,
      },
      defaultSettings: {
        defaultMethod: 'Straight Line',
        defaultPercentage: 20.0,
        defaultPeriodYears: 5,
        defaultFrequency: 'Monthly',
        doNotDepreciate: false,
        forceTransfersOnChangingCategory: false,
      },
    });

    if (getDataSource() === 'demo') {
      // In demo mode, use localStorage as fallback
      const localOptions = getLocalStorageOptions();
      if (localOptions) {
        return localOptions;
      }
      return getDefaultOptions();
    }

    // Try to fetch system options from the backend API
    let systemOptions: any = null;
    try {
      systemOptions = await this.fetchAPI('/api/Settings/system-options');
    } catch (error) {
      console.warn('Failed to fetch system options from backend, falling back to localStorage:', error);
    }

    // Get localStorage options as fallback
    const localOptions = getLocalStorageOptions();

    // Check if backend has nominal accounts data
    const backendHasNominalAccounts = systemOptions?.transactions?.nominalAccounts && (
      systemOptions.transactions.nominalAccounts.assetCost ||
      systemOptions.transactions.nominalAccounts.depreciationBS ||
      systemOptions.transactions.nominalAccounts.depreciationPL ||
      systemOptions.transactions.nominalAccounts.disposal ||
      systemOptions.transactions.nominalAccounts.revaluationBS ||
      systemOptions.transactions.nominalAccounts.revaluationPL ||
      systemOptions.transactions.nominalAccounts.disposalsBankAccount ||
      systemOptions.transactions.nominalAccounts.additionsWIPBankAcc
    );

    // If backend has no nominal accounts but localStorage does, use localStorage for transactions
    const useLocalStorageTransactions = !backendHasNominalAccounts && localOptions?.transactions?.nominalAccounts;

    // Transform backend SystemOptionsDto to frontend asset options format
    // Fall back to localStorage for transactions if backend has no data
    const result: any = {
      general: {
        assetAutoNumbering: systemOptions?.autoNumbering?.enabled ?? localOptions?.general?.assetAutoNumbering ?? false,
        numberOfChars: systemOptions?.autoNumbering?.numberOfChars ?? 10,
        currentNumber: systemOptions?.autoNumbering?.currentNumber ?? 1,
        doNotAllowNegativeBoughtValue: systemOptions?.validation?.noNegativeBoughtValue ?? localOptions?.general?.doNotAllowNegativeBoughtValue ?? false,
        defaultBaseValueOn: systemOptions?.defaults?.baseValueOn ?? localOptions?.general?.defaultBaseValueOn ?? 'remaining',
        servicePeriodDefaults: systemOptions?.defaults?.servicePeriod ?? localOptions?.general?.servicePeriodDefaults ?? 0,
      },
      defaultSettings: localOptions?.defaultSettings ?? {
        defaultMethod: 'Straight Line',
        defaultPercentage: 20.0,
        defaultPeriodYears: 5,
        defaultFrequency: 'Monthly',
        doNotDepreciate: false,
        forceTransfersOnChangingCategory: false,
      },
    };

    // Use localStorage transactions if backend has no nominal accounts data
    if (useLocalStorageTransactions) {
      result.transactions = localOptions.transactions;
      console.log('Using localStorage transactions data (backend has no nominal accounts)');
    } else {
      result.transactions = {
        allowZeroValueAdditions: systemOptions?.transactions?.allowZeroValueAdditions ?? false,
        forceAllDepreciationBeforePeriodEnd: systemOptions?.transactions?.forceAllDepreciationBeforePeriodEnd ?? false,
        doNotReverseInPeriodDepreciation: systemOptions?.transactions?.doNotReverseInPeriodDepreciation ?? false,
        nominalAccounts: {
          assetCost: systemOptions?.transactions?.nominalAccounts?.assetCost ?? null,
          depreciationBS: systemOptions?.transactions?.nominalAccounts?.depreciationBS ?? null,
          depreciationPL: systemOptions?.transactions?.nominalAccounts?.depreciationPL ?? null,
          disposal: systemOptions?.transactions?.nominalAccounts?.disposal ?? null,
          revaluationBS: systemOptions?.transactions?.nominalAccounts?.revaluationBS ?? null,
          revaluationPL: systemOptions?.transactions?.nominalAccounts?.revaluationPL ?? null,
          disposalsBankAccount: systemOptions?.transactions?.nominalAccounts?.disposalsBankAccount ?? null,
          additionsWIPBankAcc: systemOptions?.transactions?.nominalAccounts?.additionsWIPBankAcc ?? null,
        },
        costing: {
          depreciation: systemOptions?.transactions?.costing?.depreciation ?? null,
          addition: systemOptions?.transactions?.costing?.addition ?? null,
          disposals: systemOptions?.transactions?.costing?.disposals ?? null,
          revaluation: systemOptions?.transactions?.costing?.revaluation ?? null,
        },
        postCostingTransactions: {
          depreciation: systemOptions?.transactions?.postCostingTransactions?.depreciation ?? false,
          additions: systemOptions?.transactions?.postCostingTransactions?.additions ?? false,
          revaluations: systemOptions?.transactions?.postCostingTransactions?.revaluations ?? false,
          disposals: systemOptions?.transactions?.postCostingTransactions?.disposals ?? false,
        },
        postCreditToCosting: {
          depreciation: systemOptions?.transactions?.postCreditToCosting?.depreciation ?? false,
          additions: systemOptions?.transactions?.postCreditToCosting?.additions ?? false,
          revaluations: systemOptions?.transactions?.postCreditToCosting?.revaluations ?? false,
          disposals: systemOptions?.transactions?.postCreditToCosting?.disposals ?? false,
        },
      };
    }

    result.managementReportingLabels = {
      column1: systemOptions?.managementReportingLabels?.column1 ?? '',
      column2: systemOptions?.managementReportingLabels?.column2 ?? '',
      column3: systemOptions?.managementReportingLabels?.column3 ?? '',
      column4: systemOptions?.managementReportingLabels?.column4 ?? '',
      column5: systemOptions?.managementReportingLabels?.column5 ?? '',
      column6: systemOptions?.managementReportingLabels?.column6 ?? '',
      column7: systemOptions?.managementReportingLabels?.column7 ?? '',
      column8: systemOptions?.managementReportingLabels?.column8 ?? '',
      column9: systemOptions?.managementReportingLabels?.column9 ?? '',
      column10: systemOptions?.managementReportingLabels?.column10 ?? '',
    };

    return result;
  }

  async getAssetHistory(
    assetId: string,
    options?: { page?: number; pageSize?: number; eventType?: string }
  ): Promise<{ items: any[]; totalCount: number; page: number; pageSize: number; totalPages: number; hasMore: boolean }> {
    if (getDataSource() === 'demo') {
      // Import and use demo service
      const { getAssetHistoryDemo } = await import('./demo/history-demo-service');
      const items = getAssetHistoryDemo(assetId);
      return {
        items,
        totalCount: items.length,
        page: 1,
        pageSize: items.length,
        totalPages: 1,
        hasMore: false,
      };
    }
    const params = new URLSearchParams();
    if (options?.page) params.append('page', options.page.toString());
    if (options?.pageSize) params.append('pageSize', options.pageSize.toString());
    if (options?.eventType) params.append('eventType', options.eventType);
    const queryString = params.toString();
    const url = `/api/Assets/${assetId}/history${queryString ? `?${queryString}` : ''}`;
    return this.fetchAPI(url);
  }

  // ==================== TASKS (Work Items) ====================

  /**
   * Map backend WorkItemDto to frontend Task type
   */
  private mapWorkItemToTask(workItem: any): any {
    return {
      id: workItem.id,
      title: workItem.title,
      description: workItem.description,
            type: workItem.type ?? 'General Task',
            priority: workItem.priority ?? 'Medium',
            status: workItem.status ?? 'New',
      assignee: workItem.assignedToUserId ? {
        id: workItem.assignedToUserId,
        name: workItem.assignedToUserName ?? 'Unknown',
        email: workItem.assignedToUserEmail ?? '',
        avatar: workItem.assignedToUserAvatar,
        type: workItem.assignedToUserType ?? 'user',
      } : undefined,
      assignedBy: workItem.assignedByUserName,
      assignedAt: workItem.assignedAt,
      createdAt: workItem.createdDate,
      updatedAt: workItem.modifiedDate || workItem.createdDate,
      dueDate: workItem.dueDate,
      startDate: workItem.startDate,
      resolvedAt: workItem.resolvedDate,
      closedAt: workItem.closedDate,
      links: (workItem.links ?? []).map((link: any) => ({
        id: link.id,
        type: link.linkType,
        entityId: link.entityId,
        entityName: link.entityName,
        metadata: link.metadataJson ? JSON.parse(link.metadataJson) : undefined,
      })),
      tags: workItem.tags ?? [],
      checklists: (workItem.checklists ?? []).map((c: any) => ({
        id: c.id,
        title: c.title,
        completed: c.completed,
        mandatory: c.mandatory,
        createdAt: c.createdAt,
        completedAt: c.completedAt,
        completedBy: c.completedByUserName,
      })),
      subtasks: (workItem.subtasks ?? []).map((s: any) => ({
        id: s.id,
        title: s.title,
        status: s.status,
        assigneeId: s.assigneeId,
        assigneeName: s.assigneeName,
        dueDate: s.dueDate,
      })),
      createdBy: workItem.createdByUserName ?? 'System',
      updatedBy: workItem.modifiedByUserName,
      commentsCount: (workItem.comments ?? []).length,
      attachmentsCount: (workItem.attachments ?? []).length,
      lastActivityAt: workItem.lastActivityAt || workItem.modifiedDate || workItem.createdDate,
      lastActivityBy: workItem.lastActivityByUserName,
    };
  }

  /**
   * Helper to add a simple filter parameter if it exists
   */
  private addSimpleFilterParam(
    filters: any,
    params: Record<string, string>,
    filterKey: string,
    paramKey?: string
  ): void {
    const value = filters[filterKey];
    if (value !== undefined && value !== null && value !== '') {
      params[paramKey ?? filterKey] = String(value);
    }
  }

  /**
   * Helper to add a boolean flag parameter if truthy
   */
  private addBooleanFlagParam(
    filters: any,
    params: Record<string, string>,
    filterKey: string,
    paramKey: string
  ): void {
    if (filters[filterKey]) {
      params[paramKey] = 'true';
    }
  }

  /**
   * Helper to add array filter parameters with indexed keys
   */
  private addArrayFilterParam(
    filters: any,
    params: Record<string, string>,
    filterKey: string,
    paramPrefix: string
  ): void {
    const values = filters[filterKey] as string[] | undefined;
    if (!values || values.length === 0) return;
    values.forEach((v, i) => {
      params[`${paramPrefix}[${i}]`] = v;
    });
  }

  /**
   * Map frontend Task filters to backend WorkItemFilterParamsDto
   */
  private mapTaskFiltersToParams(filters: any): Record<string, string> {
    const params: Record<string, string> = {};

    // Array filters
    this.addArrayFilterParam(filters, params, 'statuses', 'statuses');
    this.addArrayFilterParam(filters, params, 'types', 'types');
    this.addArrayFilterParam(filters, params, 'priorities', 'priorities');

    // Simple mappings
    this.addSimpleFilterParam(filters, params, 'search', 'search');
    this.addSimpleFilterParam(filters, params, 'createdBy', 'createdByUserId');
    this.addSimpleFilterParam(filters, params, 'assetId', 'linkedAssetId');
    this.addSimpleFilterParam(filters, params, 'auditId', 'linkedAuditId');
    this.addSimpleFilterParam(filters, params, 'exceptionId', 'linkedExceptionId');
    this.addSimpleFilterParam(filters, params, 'locationId', 'linkedLocationId');
    this.addSimpleFilterParam(filters, params, 'sortBy', 'sortBy');
    this.addSimpleFilterParam(filters, params, 'order', 'sortOrder');
    this.addSimpleFilterParam(filters, params, 'page', 'page');
    this.addSimpleFilterParam(filters, params, 'pageSize', 'pageSize');

    // Special case: assignee (takes first from array)
    if (filters.assigneeIds && filters.assigneeIds.length > 0) {
      params.assignedToUserId = filters.assigneeIds[0];
    }

    // Boolean flags
    this.addBooleanFlagParam(filters, params, 'isOverdue', 'overdue');
    this.addBooleanFlagParam(filters, params, 'dueToday', 'dueToday');
    this.addBooleanFlagParam(filters, params, 'dueThisWeek', 'dueThisWeek');

    return params;
  }

  /**
   * Get tasks with filtering and pagination
   */
  async getTasks(filters?: any): Promise<{ data: any[]; total: number; page: number; pageSize: number; totalPages: number }> {
    if (getDataSource() === 'demo') {
      // Import and use demo service
      const { getTasksDemo } = await import('./demo/tasks-demo-service');
      return getTasksDemo(filters);
    }

    const params = filters ? this.mapTaskFiltersToParams(filters) : {};
    const queryString = new URLSearchParams(params).toString();

    return this.withDemoFallback(
      async () => {
        const result = await this.fetchAPI(`/api/WorkItems/filter?${queryString}`);
        return {
          data: (result.items ?? []).map((item: any) => this.mapWorkItemToTask(item)),
          total: result.totalCount ?? 0,
          page: result.page ?? 1,
          pageSize: result.pageSize ?? 20,
          totalPages: result.totalPages ?? 1,
        };
      },
      async () => {
        const { getTasksDemo } = await import('./demo/tasks-demo-service');
        return getTasksDemo(filters);
      },
      'getTasks'
    );
  }

  /**
   * Get tasks from API only (no demo fallback)
   */
  async getTasksApiOnly(filters?: any): Promise<{ data: any[]; total: number; page: number; pageSize: number; totalPages: number }> {
    const params = filters ? this.mapTaskFiltersToParams(filters) : {};
    const queryString = new URLSearchParams(params).toString();

    const result = await this.fetchAPI(`/api/WorkItems/filter?${queryString}`);
    return {
      data: (result.items ?? []).map((item: any) => this.mapWorkItemToTask(item)),
      total: result.totalCount ?? 0,
      page: result.page ?? 1,
      pageSize: result.pageSize ?? 20,
      totalPages: result.totalPages ?? 1,
    };
  }

  /**
   * Get a single task by ID
   */
  async getTask(id: string): Promise<any> {
    if (getDataSource() === 'demo') {
      const { getTaskDemo } = await import('./demo/tasks-demo-service');
      return getTaskDemo(id);
    }

    return this.withDemoFallback(
      async () => {
        const workItem = await this.fetchAPI(`/api/WorkItems/${id}`);
        return this.mapWorkItemToTask(workItem);
      },
      async () => {
        const { getTaskDemo } = await import('./demo/tasks-demo-service');
        return getTaskDemo(id);
      },
      'getTask'
    );
  }

  /**
   * Create a new task
   */
  async createTask(task: any): Promise<any> {
    if (getDataSource() === 'demo') {
      throw new Error('Cannot create tasks in demo mode. Please switch to API mode.');
    }

    const createDto = {
      title: task.title,
      description: task.description,
            type: task.type ?? 'General Task',
            status: task.status ?? 'New',
            priority: task.priority ?? 'Medium',
      assignedToUserId: task.assigneeId || task.assignee?.id,
      assignedToUserName: task.assignee?.name,
      assignedToUserEmail: task.assignee?.email,
      assignedToUserAvatar: task.assignee?.avatar,
      assignedToUserType: task.assignee?.type,
      dueDate: task.dueDate,
      startDate: task.startDate,
      tags: task.tags,
      links: task.links?.map((link: any) => ({
        linkType: link.type,
        entityId: link.entityId,
        entityName: link.entityName,
        metadataJson: link.metadata ? JSON.stringify(link.metadata) : undefined,
      })),
      checklists: task.checklists?.map((c: any) => ({
        title: c.title,
        mandatory: c.mandatory || false,
      })),
      subtasks: task.subtasks?.map((s: any) => ({
        title: s.title,
        assigneeId: s.assigneeId,
        assigneeName: s.assigneeName,
        dueDate: s.dueDate,
      })),
    };

    const workItem = await this.fetchAPI('/api/WorkItems', {
      method: 'POST',
      body: JSON.stringify(createDto),
    });
    return this.mapWorkItemToTask(workItem);
  }

  /**
   * Update an existing task
   */
  async updateTask(id: string, task: any): Promise<any> {
    if (getDataSource() === 'demo') {
      throw new Error('Cannot update tasks in demo mode. Please switch to API mode.');
    }

    const updateDto = {
      title: task.title,
      description: task.description,
      type: task.type,
      status: task.status,
      priority: task.priority,
      assignedToUserId: task.assigneeId || task.assignee?.id,
      assignedToUserName: task.assignee?.name,
      assignedToUserEmail: task.assignee?.email,
      assignedToUserAvatar: task.assignee?.avatar,
      assignedToUserType: task.assignee?.type,
      dueDate: task.dueDate,
      startDate: task.startDate,
      resolvedDate: task.resolvedAt,
      closedDate: task.closedAt,
      tags: task.tags,
    };

    const workItem = await this.fetchAPI(`/api/WorkItems/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updateDto),
    });
    return this.mapWorkItemToTask(workItem);
  }

  /**
   * Delete a task
   */
  async deleteTask(id: string): Promise<void> {
    if (getDataSource() === 'demo') {
      throw new Error('Cannot delete tasks in demo mode. Please switch to API mode.');
    }

    await this.fetchAPI(`/api/WorkItems/${id}`, {
      method: 'DELETE',
    });
  }

  /**
   * Get task statistics
   */
  async getTaskStatistics(): Promise<any> {
    if (getDataSource() === 'demo') {
      const { getTaskStatisticsDemo } = await import('./demo/tasks-demo-service');
      return getTaskStatisticsDemo();
    }

    return this.withDemoFallback(
      async () => {
        const stats = await this.fetchAPI('/api/WorkItems/statistics');
        return {
          totalTasks: stats.totalCount ?? 0,
          byStatus: stats.statusCounts ?? {},
          byType: stats.typeCounts ?? {},
          byPriority: stats.priorityCounts ?? {},
          overdue: stats.overdueCount ?? 0,
          dueToday: stats.dueTodayCount ?? 0,
          dueThisWeek: stats.dueThisWeekCount ?? 0,
        };
      },
      async () => {
        const { getTaskStatisticsDemo } = await import('./demo/tasks-demo-service');
        return getTaskStatisticsDemo();
      },
      'getTaskStatistics'
    );
  }

  // Task Tags
  async getTaskTags(taskId: string): Promise<string[]> {
    return this.fetchAPI(`/api/WorkItems/${taskId}/tags`);
  }

  async addTaskTag(taskId: string, tag: string): Promise<void> {
    await this.fetchAPI(`/api/WorkItems/${taskId}/tags`, {
      method: 'POST',
      body: JSON.stringify(tag),
    });
  }

  async removeTaskTag(taskId: string, tag: string): Promise<void> {
    await this.fetchAPI(`/api/WorkItems/${taskId}/tags/${encodeURIComponent(tag)}`, {
      method: 'DELETE',
    });
  }

  async setTaskTags(taskId: string, tags: string[]): Promise<void> {
    await this.fetchAPI(`/api/WorkItems/${taskId}/tags`, {
      method: 'PUT',
      body: JSON.stringify(tags),
    });
  }

  // Task Links
  async getTaskLinks(taskId: string): Promise<any[]> {
    const links = await this.fetchAPI(`/api/WorkItems/${taskId}/links`);
    return links.map((link: any) => ({
      id: link.id,
      type: link.linkType,
      entityId: link.entityId,
      entityName: link.entityName,
      metadata: link.metadataJson ? JSON.parse(link.metadataJson) : undefined,
    }));
  }

  async addTaskLink(taskId: string, link: any): Promise<any> {
    const dto = {
      linkType: link.type,
      entityId: link.entityId,
      entityName: link.entityName,
      metadataJson: link.metadata ? JSON.stringify(link.metadata) : undefined,
    };
    const result = await this.fetchAPI(`/api/WorkItems/${taskId}/links`, {
      method: 'POST',
      body: JSON.stringify(dto),
    });
    return {
      id: result.id,
      type: result.linkType,
      entityId: result.entityId,
      entityName: result.entityName,
      metadata: result.metadataJson ? JSON.parse(result.metadataJson) : undefined,
    };
  }

  async removeTaskLink(taskId: string, linkId: string): Promise<void> {
    await this.fetchAPI(`/api/WorkItems/${taskId}/links/${linkId}`, {
      method: 'DELETE',
    });
  }

  // Task Checklists
  async getTaskChecklists(taskId: string): Promise<any[]> {
    const checklists = await this.fetchAPI(`/api/WorkItems/${taskId}/checklists`);
    return checklists.map((c: any) => ({
      id: c.id,
      title: c.title,
      completed: c.completed,
      mandatory: c.mandatory,
      createdAt: c.createdAt,
      completedAt: c.completedAt,
      completedBy: c.completedByUserName,
    }));
  }

  async addTaskChecklistItem(taskId: string, item: any): Promise<any> {
    const dto = {
      title: item.title,
      mandatory: item.mandatory || false,
    };
    const result = await this.fetchAPI(`/api/WorkItems/${taskId}/checklists`, {
      method: 'POST',
      body: JSON.stringify(dto),
    });
    return {
      id: result.id,
      title: result.title,
      completed: result.completed,
      mandatory: result.mandatory,
      createdAt: result.createdAt,
      completedAt: result.completedAt,
      completedBy: result.completedByUserName,
    };
  }

  async updateTaskChecklistItem(taskId: string, checklistId: string, item: any): Promise<any> {
    const dto = {
      title: item.title,
      completed: item.completed,
      mandatory: item.mandatory,
    };
    const result = await this.fetchAPI(`/api/WorkItems/${taskId}/checklists/${checklistId}`, {
      method: 'PUT',
      body: JSON.stringify(dto),
    });
    return {
      id: result.id,
      title: result.title,
      completed: result.completed,
      mandatory: result.mandatory,
      createdAt: result.createdAt,
      completedAt: result.completedAt,
      completedBy: result.completedByUserName,
    };
  }

  async removeTaskChecklistItem(taskId: string, checklistId: string): Promise<void> {
    await this.fetchAPI(`/api/WorkItems/${taskId}/checklists/${checklistId}`, {
      method: 'DELETE',
    });
  }

  // Task Subtasks
  async getTaskSubtasks(taskId: string): Promise<any[]> {
    const subtasks = await this.fetchAPI(`/api/WorkItems/${taskId}/subtasks`);
    return subtasks.map((s: any) => ({
      id: s.id,
      title: s.title,
      status: s.status,
      assigneeId: s.assigneeId,
      assigneeName: s.assigneeName,
      dueDate: s.dueDate,
    }));
  }

  async addTaskSubtask(taskId: string, subtask: any): Promise<any> {
    const dto = {
      title: subtask.title,
      assigneeId: subtask.assigneeId,
      assigneeName: subtask.assigneeName,
      dueDate: subtask.dueDate,
    };
    const result = await this.fetchAPI(`/api/WorkItems/${taskId}/subtasks`, {
      method: 'POST',
      body: JSON.stringify(dto),
    });
    return {
      id: result.id,
      title: result.title,
      status: result.status,
      assigneeId: result.assigneeId,
      assigneeName: result.assigneeName,
      dueDate: result.dueDate,
    };
  }

  async updateTaskSubtask(taskId: string, subtaskId: string, subtask: any): Promise<any> {
    const dto = {
      title: subtask.title,
      status: subtask.status,
      assigneeId: subtask.assigneeId,
      assigneeName: subtask.assigneeName,
      dueDate: subtask.dueDate,
    };
    const result = await this.fetchAPI(`/api/WorkItems/${taskId}/subtasks/${subtaskId}`, {
      method: 'PUT',
      body: JSON.stringify(dto),
    });
    return {
      id: result.id,
      title: result.title,
      status: result.status,
      assigneeId: result.assigneeId,
      assigneeName: result.assigneeName,
      dueDate: result.dueDate,
    };
  }

  async removeTaskSubtask(taskId: string, subtaskId: string): Promise<void> {
    await this.fetchAPI(`/api/WorkItems/${taskId}/subtasks/${subtaskId}`, {
      method: 'DELETE',
    });
  }

  // Task Comments
  async getTaskComments(taskId: string): Promise<any[]> {
    if (getDataSource() === 'demo') {
      const { getTaskCommentsDemo } = await import('./demo/tasks-demo-service');
      return getTaskCommentsDemo(taskId);
    }

    return this.withDemoFallback(
      async () => {
        const comments = await this.fetchAPI(`/api/WorkItems/${taskId}/comments`);
        return comments.map((c: any) => ({
          id: c.id,
          taskId: c.workItemId,
          content: c.comment,
          createdAt: c.createdDate,
          createdBy: c.createdByUserId,
          createdByName: c.createdByUserName,
        }));
      },
      async () => {
        const { getTaskCommentsDemo } = await import('./demo/tasks-demo-service');
        return getTaskCommentsDemo(taskId);
      },
      'getTaskComments'
    );
  }

  async addTaskComment(taskId: string, comment: string): Promise<any> {
    const dto = { comment };
    const result = await this.fetchAPI(`/api/WorkItems/${taskId}/comments`, {
      method: 'POST',
      body: JSON.stringify(dto),
    });
    return {
      id: result.id,
      taskId: result.workItemId,
      content: result.comment,
      createdAt: result.createdDate,
      createdBy: result.createdByUserId,
      createdByName: result.createdByUserName,
    };
  }

  // Task History
  async getTaskHistory(taskId: string): Promise<any[]> {
    const history = await this.fetchAPI(`/api/WorkItems/${taskId}/history`);
    return history.map((h: any) => {
      const descParts: string[] = [h.action + ':'];
      if (h.fieldName) descParts.push(h.fieldName);
      if (h.oldValue) descParts.push('from "' + h.oldValue + '"');
      if (h.newValue) descParts.push('to "' + h.newValue + '"');

      return {
        id: h.id,
        taskId: h.workItemId,
        type: h.action === 'Created' ? 'status-change' : 'field-update',
        description: descParts.join(' ').trim(),
        createdAt: h.changedDate,
        createdBy: h.changedByUserId,
        createdByName: h.changedByUserName,
        oldValue: h.oldValue,
        newValue: h.newValue,
      };
    });
  }

  // Task Attachments
  async getTaskAttachments(taskId: string): Promise<any[]> {
    if (getDataSource() === 'demo') {
      const { getTaskAttachmentsDemo } = await import('./demo/tasks-demo-service');
      return getTaskAttachmentsDemo(taskId);
    }

    return this.withDemoFallback(
      async () => {
        // TODO: Implement API call when backend endpoint is available
        return [];
      },
      async () => {
        const { getTaskAttachmentsDemo } = await import('./demo/tasks-demo-service');
        return getTaskAttachmentsDemo(taskId);
      },
      'getTaskAttachments'
    );
  }

  // Task Activity
  async getTaskActivity(taskId: string): Promise<any[]> {
    if (getDataSource() === 'demo') {
      const { getTaskActivityDemo } = await import('./demo/tasks-demo-service');
      return getTaskActivityDemo(taskId);
    }

    return this.withDemoFallback(
      async () => {
        // Use task history as activity for now
        return this.getTaskHistory(taskId);
      },
      async () => {
        const { getTaskActivityDemo } = await import('./demo/tasks-demo-service');
        return getTaskActivityDemo(taskId);
      },
      'getTaskActivity'
    );
  }

  // Insurance Value Report
  async getInsuranceValueReport(filters?: {
    category?: string;
    location?: string;
    search?: string;
    sortBy?: string;
    order?: string;
  }): Promise<{
    items: Array<{
      id: string;
      assetCode: string;
      description: string;
      serialNumber?: string;
      endOfYearNBV: number;
      purchaseValue: number;
      purchaseDate: string;
      insuranceValue: number;
      category: string;
      location: string;
      status: string;
    }>;
    totalPurchaseValue: number;
    totalNetBookValue: number;
    totalInsuranceValue: number;
    totalAssets: number;
  }> {
    if (getDataSource() === 'demo') {
      // In demo mode, return mock data based on existing assets
      const result = await this.getAssets({ 
        pageSize: 1000,
        status: 'active'
      });
      
      const items = result.data.map(asset => ({
        id: asset.id,
        assetCode: asset.code ?? '',
        description: asset.name,
        serialNumber: asset.serialNumber,
        endOfYearNBV: asset.netBookValue ?? 0,
        purchaseValue: asset.acquisitionCost ?? 0,
        purchaseDate: asset.acquisitionDate || new Date().toISOString(),
        insuranceValue: asset.insuranceValue ?? 0,
        category: asset.category ?? '',
        location: asset.location ?? '',
        status: asset.status ?? 'active',
      }));

      return {
        items,
        totalPurchaseValue: items.reduce((sum, i) => sum + i.purchaseValue, 0),
        totalNetBookValue: items.reduce((sum, i) => sum + i.endOfYearNBV, 0),
        totalInsuranceValue: items.reduce((sum, i) => sum + i.insuranceValue, 0),
        totalAssets: items.length,
      };
    }

    const queryParams = new URLSearchParams();
    if (filters?.category) queryParams.append('category', filters.category);
    if (filters?.location) queryParams.append('location', filters.location);
    if (filters?.search) queryParams.append('search', filters.search);
    if (filters?.sortBy) queryParams.append('sortBy', filters.sortBy);
    if (filters?.order) queryParams.append('order', filters.order);

    const queryString = queryParams.toString();
    return this.fetchAPI(`/api/Reports/insurance-value${queryString ? `?${queryString}` : ''}`);
  }

  // Statutory Report
  async getStatutoryReport(year: number, period: number): Promise<{
    year: number;
    period: number;
    categoryLabels: string[];
    rows: Array<{
      label: string;
      companyTotal: number;
      categoryValues: Record<string, number>;
      isHeader?: boolean;
      isBold?: boolean;
    }>;
  }> {
    if (getDataSource() === 'demo') {
      // In demo mode, return mock statutory report data
      const categories = ['Computers', 'Furniture', 'Vehicles', 'Equipment'];
      
      const createRow = (label: string, isHeader = false, isBold = false) => {
        const categoryValues: Record<string, number> = {};
        let companyTotal = 0;
        
        if (!isHeader) {
          categories.forEach(cat => {
            const value = Math.round(Math.random() * 50000);
            categoryValues[cat] = value;
            companyTotal += value;
          });
        }
        
        return { label, companyTotal, categoryValues, isHeader, isBold };
      };

      return {
        year,
        period,
        categoryLabels: categories,
        rows: [
          createRow('COST', true, true),
          createRow('Start of Year Cost'),
          createRow('Addition Cost'),
          createRow('Disposals Cost'),
          createRow('Revaluation Cost'),
          createRow('End of Year Cost', false, true),
          createRow('DEPRECIATION', true, true),
          createRow('Start of Year Depreciation'),
          createRow('Charge Depreciation'),
          createRow('Disposals Depreciation'),
          createRow('Revaluation Depreciation'),
          createRow('Depreciation End of Year Cost', false, true),
          createRow('NET BOOK VALUE', true, true),
          createRow('Start of Year Net Book Value', false, true),
          createRow('End of Year Net Book Value', false, true),
        ],
      };
    }

    return this.fetchAPI(`/api/Reports/statutory?year=${year}&period=${period}`);
  }

  // Import Assets from Financials Integration
  async importAssetsFromFinancials(financialsInstanceId: string): Promise<{
    importedCount: number;
    skippedCount: number;
    errorCount: number;
    skippedAssetCodes: string[];
    errors: string[];
    message: string;
  }> {
    if (getDataSource() === 'demo') {
      // In demo mode, return a mock result
      return {
        importedCount: 0,
        skippedCount: 0,
        errorCount: 0,
        skippedAssetCodes: [],
        errors: [],
        message: 'Import from Financials is not available in demo mode. Please switch to API mode.',
      };
    }

    return this.fetchAPI(`/api/Assets/import-from-financials?financialsInstanceId=${encodeURIComponent(financialsInstanceId)}`, {
      method: 'POST',
    });
  }
}

export const dataService: any = new DataService();
export const isApiMode = (): boolean => getDataSource() === 'api';
export const isDemoMode = (): boolean => getDataSource() === 'demo';
export const getDataSourceMode = (): 'api' | 'demo' => getDataSource();
